import "bootstrap/dist/css/bootstrap.min.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import BotaoRelatorio from "../components/BotaoRelatorio";

const API_BASE_URL = process.env.REACT_APP_API_URL;

// ✅ Recurso de documentos (Nota Fiscal / Comprovante / Boleto / Contrato)
// Suspenso em 21/07/2026 e religado em 24/07/2026 a pedido do usuário.
// Trocar para false desliga de novo a coluna Documentos, o botão Contrato
// e os 4 modais. O botão Contrato agora abre o PDF assinado do contrato
// (endpoint POST /SQlCustom/ContratoDoc). Ver HISTORICO.md.
const DOCUMENTOS_HABILITADOS = false;

// ⚙️ Filtros avançados do modal de movimentos (botão "Filtros", painel de
// fornecedor/período/valor e os chips). Trocar para true religa.
// Proposital não reaproveitar a flag de documentos: são recursos diferentes e
// não faz sentido um arrastar o outro.
const FILTROS_HABILITADOS = false;

// ===== Helpers do comprovante Itaú (GED.dbo.XMLItauComprovante) =====
const H = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const firstText = (...valores) => {
  for (const v of valores) {
    if (v !== null && v !== undefined && String(v).trim() !== "") {
      return String(v).trim();
    }
  }
  return "";
};

const extrairCampoTexto = (texto, inicio, fim) => {
  if (!texto) return "";
  const i = texto.indexOf(inicio);
  if (i < 0) return "";
  const start = i + inicio.length;
  const j = texto.indexOf(fim, start);
  const trecho = j >= 0 ? texto.substring(start, j) : texto.substring(start);
  return trecho.replace(/\s+/g, " ").trim();
};

const formatDateOnly = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("pt-BR");
};

const formatMoneyComprovante = (v) =>
  v === null || v === undefined
    ? ""
    : Number(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const receiptCell = (label, valor) =>
  `<div class='cell'><div class='label'>${H(label)}</div><div class='value'>${H(
    valor
  )}</div></div>`;

// Porta do MontarHtmlComprovanteItau (C#) para JS — mesmo layout/CSS
const montarHtmlComprovanteItau = (comp) => {
  const pagador = firstText(
    comp.PagadorNome,
    extrairCampoTexto(comp.TextoExtraido, "Empresa:", "Dados do pagamento")
  );
  const pagadorDoc = firstText(
    comp.PagadorCpfCnpj,
    extrairCampoTexto(comp.TextoExtraido, "CPF/CNPJ:", "Empresa:")
  );
  const favorecido = firstText(comp.FavorecidoNome, comp.FornecedorNomeRm);
  const favorecidoDoc = firstText(
    comp.FavorecidoCpfCnpj,
    comp.FornecedorCpfCnpjRm
  );
  const agenciaConta = extrairCampoTexto(
    comp.TextoExtraido,
    "Agencia/conta:",
    "CPF/CNPJ:"
  );
  const identificacao = firstText(comp.LinhaDigitavel, comp.ChaveLan);
  const titulo = firstText(comp.TipoComprovante, "Comprovante Itau");
  const data = formatDateOnly(comp.DataPagamento);
  const valor = formatMoneyComprovante(comp.Valor);
  const statusCss =
    String(comp.StatusVinculo || "").toUpperCase() === "VINCULADO"
      ? "ok"
      : "warn";
  const origem = comp.IdLan
    ? "IDLAN " +
      comp.IdLan +
      (comp.IdMov && comp.IdMov > 0
        ? " | IDMOV " + comp.IdMov
        : " | financeiro direto")
    : "Nao vinculado";
  const retorno = comp.IdRetorno
    ? "ID " + comp.IdRetorno + " / Linha " + (comp.LinhaRetorno ?? "")
    : "";

  return `<!doctype html><html><head><meta charset='utf-8'>
<style>
body{margin:0;background:#e7e7e7;font-family:Arial,Helvetica,sans-serif;color:#111;font-size:13px}
.page{width:900px;max-width:calc(100% - 28px);margin:18px auto;background:white;padding:28px 32px 34px;box-shadow:0 1px 4px rgba(0,0,0,.18)}
.top{display:flex;align-items:flex-start;justify-content:space-between;border-bottom:2px solid #111;padding-bottom:8px}.logo{background:#111;color:white;font-weight:700;font-size:22px;border-radius:4px;padding:8px 10px}.horas{text-align:center;font-weight:800;font-size:36px;line-height:.78}.horas span{display:block;font-size:16px}
h1{font-size:20px;margin:10px 0 6px}.section-title{font-weight:700;border-bottom:3px solid #111;padding:6px 0 4px;margin-top:6px}.line{display:flex;gap:24px;flex-wrap:wrap;padding:6px 0}.strong{font-weight:700}.box{border:2px solid #111;margin-top:10px}.bank{display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #111;padding:10px}.bank-name{font-size:24px;font-weight:700}.id{font-weight:700;text-align:right}
.grid{display:grid;grid-template-columns:1.4fr 1fr .75fr;border-bottom:1px solid #111}.cell{padding:6px;border-right:1px solid #111;min-height:38px}.cell:last-child{border-right:0}.label{font-size:11px;color:#222}.value{font-size:16px;font-weight:700;margin-top:2px;word-break:break-word}.payrow{display:grid;grid-template-columns:1fr .55fr;border-bottom:1px solid #111}.payer{padding:8px;border-right:1px solid #111}.amount{padding:5px 7px;border-bottom:1px solid #111}.amount:last-child{border-bottom:0}.footer{padding:8px}.op{margin-top:18px;font-weight:700}.meta{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:18px}.tag{border:1px solid #bbb;background:#f8f8f8;padding:8px}.ok{color:#16743b}.warn{color:#a45b00}.raw{white-space:pre-wrap;font-family:Consolas,monospace;font-size:11px;margin-top:18px;border-top:1px dashed #bbb;padding-top:12px;color:#333}
</style></head><body>
<div class='page'>
<div class='top'><div class='logo'>Itau</div><div class='horas'>30<span>horas</span></div></div>
<h1>${H(titulo)}</h1>
<div class='section-title'>Dados da conta debitada / Pagador Final</div>
<div class='line'><div>Agencia/conta: <span class='strong'>${H(
    agenciaConta
  )}</span></div><div>CPF/CNPJ: <span class='strong'>${H(
    pagadorDoc
  )}</span></div><div>Empresa: <span class='strong'>${H(
    pagador
  )}</span></div></div>
<div class='section-title'>Dados do pagamento</div>
<div class='line'><div>Identificacao no meu comprovante: <span class='strong'>${H(
    identificacao
  )}</span></div></div>
<div class='box'>
<div class='bank'><div class='bank-name'>Itau Unibanco S.A.</div><div class='id'>${H(
    identificacao
  )}</div></div>
<div class='grid'>
${receiptCell("Beneficiario", favorecido)}
${receiptCell("CPF/CNPJ do beneficiario", favorecidoDoc)}
${receiptCell("Data de vencimento", data)}
${receiptCell("Razao Social", favorecido)}
${receiptCell("Documento RM", comp.NumeroDocumento)}
${receiptCell("Valor do boleto (R$)", valor)}
</div>
<div class='payrow'><div class='payer'><div class='label'>Pagador</div><div class='value'>${H(
    pagador
  )}</div><div class='label' style='margin-top:8px'>CPF/CNPJ do pagador</div><div class='value'>${H(
    pagadorDoc
  )}</div></div>
<div><div class='amount'><div class='label'>Valor do pagamento (R$)</div><div class='value'>${H(
    valor
  )}</div></div><div class='amount'><div class='label'>Data de pagamento</div><div class='value'>${H(
    data
  )}</div></div><div class='amount'><div class='label'>Pagamento realizado em especie</div><div class='value'>Nao</div></div></div></div>
<div class='footer'><div class='label'>Autenticacao mecanica</div><div class='strong'>${H(
    comp.Autenticacao
  )}</div></div>
</div>
<div class='op'>Operacao efetuada em ${H(data)} via Sispag.</div>
<div class='meta'><div class='tag'>Status: <span class='${statusCss} strong'>${H(
    comp.StatusVinculo
  )}</span></div><div class='tag'>Origem RM: <span class='strong'>${H(
    origem
  )}</span></div><div class='tag'>Retorno: <span class='strong'>${H(
    retorno
  )}</span></div></div>
<div class='raw'>${H(comp.TextoExtraido)}</div>
</div></body></html>`;
};

// ✅ BUSCA DO MODAL "LISTAGEM DE MOVIMENTOS"

// Remove acentos e caixa para a comparação ser tolerante ("ACAO" acha "Ação")
const normalizarTexto = (valor) =>
  String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ") // NBSP do "R$ 1,00" vira espaço comum
    .toLowerCase()
    .trim();

const moedaParaTexto = (valor) =>
  Number(valor ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

// Pill do totalizador: fica vermelha quando a soma é negativa (estornos)
const classeTotalMovimento = (modificador, valor) =>
  `totais-movimentos__item totais-movimentos__item--${modificador}` +
  (valor < 0 ? " totais-movimentos__item--negativo" : "");

// Um valor é indexado em três formas, porque cada pessoa digita de um jeito:
// número cru ("16163.43"), formatado ("R$ 16.163,43") e sem separador de
// milhar ("R$ 16163,43").
const textoBuscavelValor = (valor) => {
  const numero = Number(valor ?? 0);
  const formatado = moedaParaTexto(numero);
  return `${numero} ${formatado} ${formatado.replace(/\./g, "")}`;
};

// Texto pesquisável de cada coluna.
const camposFiltraveisMovimento = (mov) => ({
  idMov: String(mov.idMov ?? ""),
  data: String(mov.data ?? ""),
  fornecedor: [
    mov.fornecedor,
    mov.origem !== "N/A" ? mov.origem : "",
    mov.destino !== "N/A" ? mov.destino : "",
  ].join(" "),
  valor: textoBuscavelValor(mov.valor),
  empenhado: textoBuscavelValor(mov.empenhado),
  status: String(mov.status ?? ""),
  tipo: String(mov.tipo ?? ""),
});

// ✅ ORDENAÇÃO DO MODAL "LISTAGEM DE MOVIMENTOS"
// Padrão: data crescente (mais antiga primeiro), igual ao ORDER BY da consulta
const ORDENACAO_MOV_PADRAO = { coluna: "data", direcao: "asc" };

// `data` já chega formatada como "dd/mm/aaaa", que não ordena como texto.
// Usamos a data crua (dataOrig) e, se faltar, remontamos a partir do exibido.
const dataMovimentoParaNumero = (mov) => {
  if (mov.dataOrig) {
    const tempo = new Date(mov.dataOrig).getTime();
    if (!Number.isNaN(tempo)) return tempo;
  }
  const partes = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(mov.data ?? ""));
  if (partes) {
    return new Date(`${partes[3]}-${partes[2]}-${partes[1]}T00:00:00`).getTime();
  }
  return 0; // "N/A" e datas inválidas vão para o começo na ordem crescente
};

const VALOR_ORDENACAO_MOVIMENTO = {
  idMov: (mov) => Number(mov.idMov) || 0,
  data: dataMovimentoParaNumero,
  fornecedor: (mov) => normalizarTexto(mov.fornecedor),
  valor: (mov) => Number(mov.valor) || 0,
  empenhado: (mov) => Number(mov.empenhado) || 0,
  status: (mov) => normalizarTexto(mov.status),
  tipo: (mov) => normalizarTexto(mov.tipo),
};

const compararMovimentos = (a, b, coluna) => {
  const extrair = VALOR_ORDENACAO_MOVIMENTO[coluna];
  if (!extrair) return 0;

  const valorA = extrair(a);
  const valorB = extrair(b);

  if (typeof valorA === "number" && typeof valorB === "number") {
    return valorA - valorB;
  }
  return String(valorA).localeCompare(String(valorB), "pt-BR");
};

// ✅ FILTROS AVANÇADOS DO MODAL "LISTAGEM DE MOVIMENTOS"
// Complementam a busca livre (que é texto solto em qualquer coluna) com o que
// texto não resolve: fornecedor exato, intervalo de data e de valor. Ficam num
// painel que abre sob demanda — a faixa fixa de inputs por coluna já foi
// tentada e pesou demais no visual (ver HISTORICO de 03/07).
const FILTROS_MOV_VAZIO = {
  fornecedor: "",
  dataDe: "",
  dataAte: "",
  valorMin: "",
  valorMax: "",
  somenteComValor: false,
  somenteComEmpenhado: false,
};

// Coluna que cada filtro destaca no cabeçalho da tabela
const COLUNA_DO_FILTRO = {
  fornecedor: "fornecedor",
  dataDe: "data",
  dataAte: "data",
  valorMin: "valor",
  valorMax: "valor",
  somenteComValor: "valor",
  somenteComEmpenhado: "empenhado",
};

// Aceita "1234,56", "1.234,56", "R$ 1.234,56" e "1234.56" — cada um digita de
// um jeito. Campo vazio (ou impossível de ler) vira null, ou seja: sem limite.
const numeroDoFiltro = (texto) => {
  const limpo = String(texto ?? "")
    .replace(/[R$\s]/gi, "")
    .trim();
  if (!limpo) return null;
  // o ponto só é separador de milhar quando fecha um grupo de 3 dígitos:
  // "1.234,56" perde o ponto, "1234.56" mantém
  const numero = Number(
    limpo.replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", ".")
  );
  return Number.isNaN(numero) ? null : numero;
};

// "aaaa-mm-dd" do <input type="date"> vira timestamp. O limite superior pega o
// fim do dia, senão "até 03/02" jogaria fora os movimentos do próprio dia 03/02.
const dataDoFiltro = (texto, fimDoDia = false) => {
  if (!texto) return null;
  const tempo = new Date(
    `${texto}T${fimDoDia ? "23:59:59.999" : "00:00:00"}`
  ).getTime();
  return Number.isNaN(tempo) ? null : tempo;
};

const dataFiltroParaTexto = (iso) => {
  const [ano, mes, dia] = String(iso).split("-");
  return dia ? `${dia}/${mes}/${ano}` : iso;
};

// Um movimento passa quando atende a TODOS os filtros preenchidos (E entre si,
// e depois E com a busca livre).
const movimentoPassaNosFiltros = (mov, filtros) => {
  if (filtros.fornecedor && mov.fornecedor !== filtros.fornecedor) return false;

  const valor = Number(mov.valor) || 0;
  const empenhado = Number(mov.empenhado) || 0;

  // "somente com" olha != 0, não > 0: estorno é negativo, mas tem valor
  if (filtros.somenteComValor && valor === 0) return false;
  if (filtros.somenteComEmpenhado && empenhado === 0) return false;

  const minimo = numeroDoFiltro(filtros.valorMin);
  const maximo = numeroDoFiltro(filtros.valorMax);
  if (minimo !== null && valor < minimo) return false;
  if (maximo !== null && valor > maximo) return false;

  const de = dataDoFiltro(filtros.dataDe);
  const ate = dataDoFiltro(filtros.dataAte, true);
  if (de !== null || ate !== null) {
    // mesma função da ordenação: usa dataOrig e cai no "dd/mm/aaaa" exibido
    const data = dataMovimentoParaNumero(mov);
    // sem data legível (N/A) o movimento fica fora de qualquer período
    if (!data) return false;
    if (de !== null && data < de) return false;
    if (ate !== null && data > ate) return false;
  }

  return true;
};

// Vira os chips acima da tabela — cada um removível pela sua chave
const descreverFiltrosAtivos = (filtros) => {
  const chips = [];
  const minimo = numeroDoFiltro(filtros.valorMin);
  const maximo = numeroDoFiltro(filtros.valorMax);

  if (filtros.fornecedor) {
    chips.push({
      chave: "fornecedor",
      rotulo: `Fornecedor: ${filtros.fornecedor}`,
    });
  }
  if (filtros.dataDe) {
    chips.push({
      chave: "dataDe",
      rotulo: `A partir de ${dataFiltroParaTexto(filtros.dataDe)}`,
    });
  }
  if (filtros.dataAte) {
    chips.push({
      chave: "dataAte",
      rotulo: `Até ${dataFiltroParaTexto(filtros.dataAte)}`,
    });
  }
  if (minimo !== null) {
    chips.push({ chave: "valorMin", rotulo: `Valor ≥ ${moedaParaTexto(minimo)}` });
  }
  if (maximo !== null) {
    chips.push({ chave: "valorMax", rotulo: `Valor ≤ ${moedaParaTexto(maximo)}` });
  }
  if (filtros.somenteComValor) {
    chips.push({ chave: "somenteComValor", rotulo: "Somente com valor líquido" });
  }
  if (filtros.somenteComEmpenhado) {
    chips.push({ chave: "somenteComEmpenhado", rotulo: "Somente com empenhado" });
  }

  return chips;
};

export default function BudgetDashboard() {
  const [data, setData] = useState([]);
  const [dataTotal, setDataTotal] = useState([]);

  // ✅ ESTADO PARA OS MOVIMENTOS
  const [movimentosData, setMovimentosData] = useState([]);
  const [loadingMovimentos, setLoadingMovimentos] = useState(false);

  // ✅ ESTADO DA BUSCA E DA ORDENAÇÃO DO MODAL DE MOVIMENTOS
  const [buscaMovimentos, setBuscaMovimentos] = useState("");
  const [ordenacaoMovimentos, setOrdenacaoMovimentos] =
    useState(ORDENACAO_MOV_PADRAO);
  const [colunaHoverMovimentos, setColunaHoverMovimentos] = useState(null);
  const [filtrosMovimentos, setFiltrosMovimentos] = useState(FILTROS_MOV_VAZIO);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  const buscaMovimentosAtiva = buscaMovimentos.trim() !== "";

  // Chips dos filtros avançados ativos; a contagem vira o badge do botão
  const filtrosAtivos = useMemo(
    () => descreverFiltrosAtivos(filtrosMovimentos),
    [filtrosMovimentos]
  );
  const temFiltroAvancado = filtrosAtivos.length > 0;
  const movimentosEstaoFiltrados = buscaMovimentosAtiva || temFiltroAvancado;

  // Fornecedores da lista já carregada, com quantos movimentos cada um tem —
  // vira o select, que evita digitar o nome e errar acento/grafia
  const fornecedoresMovimentos = useMemo(() => {
    const contagem = new Map();
    movimentosData.forEach((mov) => {
      const nome = mov.fornecedor || "N/A";
      contagem.set(nome, (contagem.get(nome) || 0) + 1);
    });
    return [...contagem.entries()]
      .map(([nome, quantidade]) => ({ nome, quantidade }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [movimentosData]);

  const movimentosFiltrados = useMemo(() => {
    const busca = normalizarTexto(buscaMovimentos);

    // filtros avançados primeiro (comparação de número, mais barata) e a
    // busca livre por cima do que sobrou
    const porFiltro = temFiltroAvancado
      ? movimentosData.filter((movimento) =>
          movimentoPassaNosFiltros(movimento, filtrosMovimentos)
        )
      : movimentosData;

    const filtrados = !busca
      ? porFiltro
      : porFiltro.filter((movimento) =>
          Object.values(camposFiltraveisMovimento(movimento)).some((campo) =>
            normalizarTexto(campo).includes(busca)
          )
        );

    const { coluna, direcao } = ordenacaoMovimentos;
    if (!coluna) return filtrados;

    const fator = direcao === "asc" ? 1 : -1;
    // cópia: sort() altera o array e não podemos mexer em movimentosData
    return [...filtrados].sort(
      (a, b) => compararMovimentos(a, b, coluna) * fator
    );
  }, [
    movimentosData,
    buscaMovimentos,
    ordenacaoMovimentos,
    filtrosMovimentos,
    temFiltroAvancado,
  ]);

  // Colunas em que a busca casou — são essas que ficam destacadas, para o
  // usuário enxergar de onde veio o resultado
  const colunasComResultado = useMemo(() => {
    const colunas = new Set();
    const busca = normalizarTexto(buscaMovimentos);
    if (!busca) return colunas;

    movimentosFiltrados.forEach((movimento) => {
      Object.entries(camposFiltraveisMovimento(movimento)).forEach(
        ([coluna, campo]) => {
          if (normalizarTexto(campo).includes(busca)) colunas.add(coluna);
        }
      );
    });
    return colunas;
  }, [movimentosFiltrados, buscaMovimentos]);

  // Colunas com filtro avançado ativo — destacadas junto com as da busca
  const colunasComFiltro = useMemo(() => {
    const colunas = new Set();
    filtrosAtivos.forEach(({ chave }) => {
      const coluna = COLUNA_DO_FILTRO[chave];
      if (coluna) colunas.add(coluna);
    });
    return colunas;
  }, [filtrosAtivos]);

  // Totalizador do rodapé — soma sempre o que está visível (lista já filtrada)
  const totaisMovimentos = useMemo(
    () =>
      movimentosFiltrados.reduce(
        (acc, mov) => {
          const realizado = Number(mov.valor) || 0;
          const empenhado = Number(mov.empenhado) || 0;
          return {
            realizado: acc.realizado + realizado,
            empenhado: acc.empenhado + empenhado,
            total: acc.total + realizado + empenhado,
          };
        },
        { realizado: 0, empenhado: 0, total: 0 }
      ),
    [movimentosFiltrados]
  );

  // Abrir/fechar o modal volta busca, filtros e ordenação ao padrão
  const resetarModalMovimentos = () => {
    setBuscaMovimentos("");
    setOrdenacaoMovimentos(ORDENACAO_MOV_PADRAO);
    setColunaHoverMovimentos(null);
    setFiltrosMovimentos(FILTROS_MOV_VAZIO);
    setFiltrosAbertos(false);
  };

  const alterarFiltroMovimentos = (chave, valor) =>
    setFiltrosMovimentos((anterior) => ({ ...anterior, [chave]: valor }));

  // O ✕ do chip devolve aquela chave ao valor vazio dela ("" ou false)
  const removerFiltroMovimentos = (chave) =>
    alterarFiltroMovimentos(chave, FILTROS_MOV_VAZIO[chave]);

  const limparFiltrosMovimentos = () => setFiltrosMovimentos(FILTROS_MOV_VAZIO);

  // 1º clique na coluna ordena (crescente), clique seguinte inverte
  const alternarOrdenacaoMovimentos = (coluna) => {
    setOrdenacaoMovimentos((anterior) =>
      anterior.coluna === coluna
        ? { coluna, direcao: anterior.direcao === "asc" ? "desc" : "asc" }
        : { coluna, direcao: "asc" }
    );
  };

  // Cabeçalho clicável: ordena e fica destacado quando a busca casou nele.
  // A seta aparece só na coluna ordenada (ou apagada na que está sob o mouse).
  const renderThMovimento = (coluna, rotulo, minWidth) => {
    const buscaCasou = colunasComResultado.has(coluna);
    const filtrada = colunasComFiltro.has(coluna);
    const destacada = buscaCasou || filtrada;
    const ordenado = ordenacaoMovimentos.coluna === coluna;
    const crescente = ordenacaoMovimentos.direcao === "asc";
    const sobMouse = colunaHoverMovimentos === coluna;

    const dicas = [];
    if (buscaCasou) dicas.push(`A busca encontrou resultados em ${rotulo}`);
    if (filtrada) dicas.push(`Filtro ativo em ${rotulo}`);
    dicas.push(
      ordenado
        ? `Ordenado ${crescente ? "crescente" : "decrescente"} — clique para inverter`
        : `Clique para ordenar por ${rotulo}`
    );

    return (
      <th
        role="button"
        onClick={() => alternarOrdenacaoMovimentos(coluna)}
        onMouseEnter={() => setColunaHoverMovimentos(coluna)}
        onMouseLeave={() => setColunaHoverMovimentos(null)}
        style={{
          minWidth,
          cursor: "pointer",
          userSelect: "none",
          whiteSpace: "nowrap",
          ...(destacada
            ? {
                backgroundColor: "#ffc107",
                color: "#212529",
                boxShadow: "none",
              }
            : {}),
        }}
        title={dicas.join(" | ")}
      >
        {destacada && "🔎 "}
        {rotulo}
        {/* span sempre presente: reserva o espaço e evita a coluna "pular" */}
        <span
          className="ms-1"
          style={{
            opacity: ordenado ? 1 : sobMouse ? 0.55 : 0,
            transition: "opacity .12s",
          }}
        >
          {ordenado ? (crescente ? "▲" : "▼") : "⇅"}
        </span>
      </th>
    );
  };


  // ✅ ESTADO PARA OS ITENS DO MOVIMENTO
  const [itensMovimento, setItensMovimento] = useState([]);
  const [loadingItens, setLoadingItens] = useState(false);

  // ✅ ESTADO PARA O MODAL DE COMPROVANTE (GED - Itaú)
  const [modalComprovanteVisible, setModalComprovanteVisible] = useState(false);
  const [comprovantesData, setComprovantesData] = useState([]);
  const [comprovanteSelecionado, setComprovanteSelecionado] = useState(0);
  const [loadingComprovante, setLoadingComprovante] = useState(false);

  // ✅ ESTADO PARA O MODAL DE BOLETO (Qive)
  const [modalBoletoVisible, setModalBoletoVisible] = useState(false);
  const [boletosData, setBoletosData] = useState([]);
  const [boletoSelecionado, setBoletoSelecionado] = useState(0);
  const [loadingBoleto, setLoadingBoleto] = useState(false);
  const [boletoPdfUrl, setBoletoPdfUrl] = useState(null);

  // ✅ ESTADO PARA O MODAL DE NOTA FISCAL (Qive)
  const [modalNfVisible, setModalNfVisible] = useState(false);
  const [notasFiscaisData, setNotasFiscaisData] = useState([]);
  const [nfSelecionada, setNfSelecionada] = useState(0);
  const [loadingNf, setLoadingNf] = useState(false);
  const [nfPdfUrl, setNfPdfUrl] = useState(null);

  // ✅ ESTADO PARA O MODAL DE CONTRATO (documento assinado - MAN_CNT_DOCUMENTOS_BINARIOS)
  const [modalContratoVisible, setModalContratoVisible] = useState(false);
  const [loadingContrato, setLoadingContrato] = useState(false);
  const [contratoPdfUrl, setContratoPdfUrl] = useState(null);
  const [contratoErro, setContratoErro] = useState(null);
  const [contratoMovAtual, setContratoMovAtual] = useState(null);

  // ✅ DISPONIBILIDADE DE DOCUMENTOS POR IDMOV { idMov: { nf, boleto, comprovante } }
  const [documentosDisponiveis, setDocumentosDisponiveis] = useState({});
  const [loadingDocs, setLoadingDocs] = useState(false);

  // ✅ ESTADOS PARA LOADING
  const [salvandoObservacao, setSalvandoObservacao] = useState(false);
  const [salvandoAlerta, setSalvandoAlerta] = useState(false);

  // ✅ ESTADOS PARA O SISTEMA DE VERSÕES
  const [modalVersaoVisible, setModalVersaoVisible] = useState(null);
  const [versoesData, setVersoesData] = useState([]);
  const [loadingVersoes, setLoadingVersoes] = useState(false);
  const [versaoDetalhada, setVersaoDetalhada] = useState(null);

  // ✅ ADICIONAR ESTES ESTADOS NO INÍCIO DO COMPONENTE (após os outros estados)
  const [modalAnoAnteriorVisible, setModalAnoAnteriorVisible] = useState(null);
  const [dadosAnoAnterior, setDadosAnoAnterior] = useState(null);
  const [loadingAnoAnterior, setLoadingAnoAnterior] = useState(false);

  // Estados para filtros
  const [filtros, setFiltros] = useState({
    ano: new Date().getFullYear(),
    tipo: "Despesas",
    centroCusto: "",
    natureza: "",
  });

  // Estados para opções dos selects
  const [opcoesCentroCusto, setOpcoesCentroCusto] = useState([]);
  const [opcoesNatureza, setOpcoesNatureza] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTotais, setLoadingTotais] = useState(false);

  // Estados dos modais
  const [modalIndex, setModalIndex] = useState(null);
  const [modalJustIndex, setModalJustIndex] = useState(null);
  const [modalListVisible, setModalListVisible] = useState(null);
  const [modalItensVisible, setModalItensVisible] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // 🔒 Trava a rolagem da página enquanto qualquer modal está aberto.
  // Estes modais são montados na mão (`<div className="modal d-block">`), sem o
  // JS do Bootstrap — e é ele quem normalmente põe `modal-open` no <body>. Sem
  // isso, a página continua rolando atrás: a barra da janela fica visível ao
  // lado da barra da tabela, e a roda do mouse sobre as bordas do modal move o
  // fundo em vez da lista.
  const algumModalAberto =
    modalListVisible !== null ||
    modalItensVisible !== null ||
    modalIndex !== null ||
    modalJustIndex !== null ||
    modalVersaoVisible !== null ||
    modalAnoAnteriorVisible !== null ||
    modalNfVisible ||
    modalBoletoVisible ||
    modalComprovanteVisible ||
    modalContratoVisible;

  useEffect(() => {
    if (!algumModalAberto) return;

    const overflowAnterior = document.body.style.overflow;
    const paddingAnterior = document.body.style.paddingRight;
    // Compensa a barra que some, senão o conteúdo do fundo dá um pulo de
    // ~15px para a direita no instante em que o modal abre.
    const larguraBarra = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (larguraBarra > 0) {
      document.body.style.paddingRight = `${larguraBarra}px`;
    }

    return () => {
      document.body.style.overflow = overflowAnterior;
      document.body.style.paddingRight = paddingAnterior;
    };
  }, [algumModalAberto]);

  const PpCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
  const PpCODFILIAL = localStorage.getItem("PCODFILIAL");
  const PpANO = localStorage.getItem("selectedYear");
  const PpTIPO = "Despesas";
  const PpCODUSUARIO = localStorage.getItem("username");

  // ✅ NOVOS ESTADOS PARA O MODAL DE LOADING DOS RELATÓRIOS
  const [relatorioLoading, setRelatorioLoading] = useState(false);
  const [relatorioTipo, setRelatorioTipo] = useState("");
  const [relatorioNome, setRelatorioNome] = useState("");
  const [relatorioProgresso, setRelatorioProgresso] = useState(0);
  const [relatorioMensagem, setRelatorioMensagem] = useState("");
  const [relatorioResultado, setRelatorioResultado] = useState(null);
  const [relatorioInterval, setRelatorioInterval] = useState(null);

  // ✅ FUNÇÃO PARA INICIAR GERAÇÃO DO RELATÓRIO
  const iniciarGeracaoRelatorio = useCallback(
    (tipo, nome) => {
      // ✅ MENSAGENS MOTIVACIONAIS PARA O LOADING
      const mensagensMotivacionais = [
        "📊 Compilando dados financeiros...",
        "🔍 Analisando movimentações orçamentárias...",
        "📈 Processando informações gerenciais...",
        "💰 Consolidando valores por centro de custo...",
        "📋 Formatando relatório para visualização...",
        "🎯 Preparando insights para tomada de decisão...",
        "⚡ Otimizando dados para melhor performance...",
        "🔄 Sincronizando informações mais recentes...",
      ];

      setRelatorioLoading(true);
      setRelatorioTipo(tipo);
      setRelatorioNome(nome);
      setRelatorioProgresso(0);
      setRelatorioMensagem(mensagensMotivacionais[0]);
      setRelatorioResultado(null);

      // Simular progresso
      let progresso = 0;
      let mensagemIndex = 0;

      const intervalo = setInterval(() => {
        progresso += Math.random() * 15 + 5; // Incremento aleatório entre 5-20%
        mensagemIndex = Math.floor(
          (progresso / 100) * mensagensMotivacionais.length
        );

        if (progresso >= 95) {
          progresso = 95; // Não deixar chegar a 100% até terminar
          clearInterval(intervalo);
        }

        setRelatorioProgresso(Math.min(progresso, 95));
        setRelatorioMensagem(
          mensagensMotivacionais[
            Math.min(mensagemIndex, mensagensMotivacionais.length - 1)
          ]
        );
      }, 800);

      // Guardar o intervalo para limpeza
      setRelatorioInterval(intervalo);
    },
    []
  );

  // ✅ FUNÇÃO PARA FINALIZAR GERAÇÃO DO RELATÓRIO
  const finalizarGeracaoRelatorio = useCallback(
    (sucesso, mensagem) => {
      // Limpar intervalo se existir
      if (relatorioInterval) {
        clearInterval(relatorioInterval);
        setRelatorioInterval(null);
      }

      setRelatorioProgresso(100);
      setRelatorioMensagem(
        sucesso ? "✅ Relatório gerado com sucesso!" : "❌ Erro na geração"
      );
      setRelatorioResultado({ sucesso, mensagem });

      // Fechar modal após 2 segundos se sucesso, ou manter aberto se erro
      setTimeout(() => {
        if (sucesso) {
          fecharModalRelatorio();
        }
      }, 2000);
    },
    [relatorioInterval]
  );

  // ✅ FUNÇÃO PARA FECHAR MODAL DE RELATÓRIO
  const fecharModalRelatorio = useCallback(() => {
    if (relatorioInterval) {
      clearInterval(relatorioInterval);
      setRelatorioInterval(null);
    }

    setRelatorioLoading(false);
    setRelatorioTipo("");
    setRelatorioNome("");
    setRelatorioProgresso(0);
    setRelatorioMensagem("");
    setRelatorioResultado(null);
  }, [relatorioInterval]);

  const itemsPerPage = 200;
  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ✅ FUNÇÃO SIMPLES PARA ATUALIZAR ORÇAMENTO
  const atualizarValorOrcamento = async (
    idOrcto,
    novoValor,
    usuario,
    dataAlteracao
  ) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        return { success: false, message: "Token não encontrado" };
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const dados = {
        IdOrcto: idOrcto,
        valor: novoValor,
        usuario: usuario,
        dataAlteracao: dataAlteracao,
      };

      console.log("💾 Salvando alteração:", dados);

      const response = await fetch(
        `${API_BASE_URL}/SQlCustom/SQLCustomUpdate`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(dados),
        }
      );

      const result = await response.json();

      if (response.ok) {
        console.log("✅ Valor atualizado com sucesso");
        return { success: true, data: result };
      } else {
        console.error("❌ Erro ao atualizar:", result);
        return { success: false, message: result.error || "Erro desconhecido" };
      }
    } catch (error) {
      console.error("❌ Erro na requisição:", error);
      return { success: false, message: error.message };
    }
  };

  // ✅ FUNÇÃO PARA ATUALIZAR OBSERVAÇÃO
  const atualizarObservacao = useCallback(
    async (idOrcto, novaObservacao, usuario, dataAlteracao) => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          return { success: false, message: "Token não encontrado" };
        }

        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        const dados = {
          IdOrcto: idOrcto,
          observacao: novaObservacao,
          usuario: usuario,
          dataAlteracao: dataAlteracao,
        };

        console.log("💾 Salvando observação:", dados);

        const response = await fetch(
          `${API_BASE_URL}/SQlCustom/SQLCustomUpdateObs`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(dados),
          }
        );

        const result = await response.json();

        if (response.ok) {
          console.log("✅ Observação atualizada com sucesso");
          return { success: true, data: result };
        } else {
          console.error("❌ Erro ao atualizar observação:", result);
          return {
            success: false,
            message: result.error || "Erro desconhecido",
          };
        }
      } catch (error) {
        console.error("❌ Erro na requisição de observação:", error);
        return { success: false, message: error.message };
      }
    },
    []
  );

  // ✅ NOVA FUNÇÃO PARA ATUALIZAR ALERTA
  const atualizarAlerta = useCallback(
    async (
      idOrcto,
      alert25,
      alert50,
      alert75,
      alert100,
      usuario,
      dataAlteracao
    ) => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          return { success: false, message: "Token não encontrado" };
        }

        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        const dados = {
          IdOrcto: idOrcto,
          alert25: alert25,
          alert50: alert50,
          alert75: alert75,
          alert100: alert100,
          usuario: usuario,
          dataAlteracao: dataAlteracao,
        };

        console.log("💾 Salvando alertas:", dados);

        const response = await fetch(
          `${API_BASE_URL}/SQlCustom/SQLCustomUpdateAlert`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(dados),
          }
        );

        const result = await response.json();

        if (response.ok) {
          console.log("✅ Alertas atualizados com sucesso");
          return { success: true, data: result };
        } else {
          console.error("❌ Erro ao atualizar alertas:", result);
          return {
            success: false,
            message: result.error || "Erro desconhecido",
          };
        }
      } catch (error) {
        console.error("❌ Erro na requisição de alertas:", error);
        return { success: false, message: error.message };
      }
    },
    []
  );

  // ✅ 1. ADICIONE ESTA FUNÇÃO NO INÍCIO DO COMPONENTE (após os states)
  const podeEditarOrcamento = () => {
    const anoSelecionado = parseInt(
      localStorage.getItem("selectedYear") || new Date().getFullYear()
    );
    return anoSelecionado >= localStorage.getItem("selectedTrava"); //anoSelecionado > 2025; // Só permite edição para anos futuros (2026+)
  };

  // ✅ 2. ADICIONE ESTA FUNÇÃO PARA VALIDAR ANTES DE SALVAR
  const validarAnoEdicao = () => {
    const anoSelecionado = parseInt(
      localStorage.getItem("selectedYear") || new Date().getFullYear()
    );
    if (anoSelecionado <= (localStorage.getItem("selectedTrava") - 1) /*2025*/) {
      alert(
        `⚠️ Não é possível alterar orçamentos para o ano ${anoSelecionado}.\nApenas anos futuros (${localStorage.getItem("selectedTrava")} em diante) podem ser editados.`
      );
      return false;
    }
    return true;
  };

  // ✅ FUNÇÃO handleOrcadoChange SIMPLIFICADA - SUBSTITUA A SUA POR ESTA:

const handleOrcadoChange = async (index, valorNumerico) => {
  // ✅ VALIDAÇÃO DE ANO
  if (!validarAnoEdicao()) {
    const globalIndex = (currentPage - 1) * itemsPerPage + index;
    const item = data[globalIndex];
    const inputElement = document.activeElement;
    if (inputElement) {
      inputElement.value = `R$${item.orcado.toFixed(2)}`;
      inputElement.blur();
    }
    return;
  }

  const globalIndex = (currentPage - 1) * itemsPerPage + index;
  const newData = [...data];
  const item = newData[globalIndex];
  const oldValue = item.orcado;

  // ✅ GARANTIR QUE É NÚMERO
  const novoValor = typeof valorNumerico === 'number' ? valorNumerico : parseFloat(valorNumerico) || 0;

  console.log('💾 handleOrcadoChange chamada:', {
    index,
    oldValue,
    novoValor,
    tipo: typeof valorNumerico,
    IdOrcto: item.IdOrcto
  });

  // ✅ VERIFICAÇÃO PRECISA DE MUDANÇA
  if (Math.abs(novoValor - oldValue) < 0.01) {
    console.log('📋 Valor não alterado (diferença < 0.01)');
    return;
  }

  // Feedback visual
  const inputElement = document.activeElement;
  if (inputElement) {
    inputElement.style.backgroundColor = "#fff3cd";
    inputElement.disabled = true;
  }

  try {
    // Obter dados do usuário
    const token = localStorage.getItem("authToken");
    const tokenPayload = JSON.parse(atob(token.split(".")[1]));
    const currentUsername = tokenPayload.username || tokenPayload.id;
    const agora = new Date().toISOString();

    console.log('🔄 Chamando API para salvar:', {
      IdOrcto: item.IdOrcto,
      valorAntigo: oldValue,
      valorNovo: novoValor,
      usuario: currentUsername
    });

    const resultado = await atualizarValorOrcamento(
      item.IdOrcto,
      novoValor,
      currentUsername,
      agora
    );

    if (resultado.success) {
      // ✅ SUCESSO - Atualizar estado local
      newData[globalIndex].orcado = novoValor;
      newData[globalIndex].resultado = novoValor - item.realizado - item.empenhado;
      setData(newData);

      // Feedback visual de sucesso
      if (inputElement) {
        inputElement.style.backgroundColor = "#d1edff";
        inputElement.style.border = "2px solid #28a745";
        inputElement.value = `R$${novoValor.toFixed(2)}`;

        setTimeout(() => {
          inputElement.style.backgroundColor = "";
          inputElement.style.border = "";
          inputElement.disabled = false;
        }, 1500);
      }

      // Atualizar totais
      await atualizarTotais();

      console.log("✅ Valor salvo com sucesso!");
    } else {
      // ❌ ERRO - Reverter
      console.error('❌ Erro da API:', resultado.message);
      
      if (inputElement) {
        inputElement.style.backgroundColor = "#f8d7da";
        inputElement.style.border = "2px solid #dc3545";
        inputElement.value = `R$${oldValue.toFixed(2)}`;

        setTimeout(() => {
          inputElement.style.backgroundColor = "";
          inputElement.style.border = "";
          inputElement.disabled = false;
        }, 2000);
      }

      alert(`❌ Erro: ${resultado.message}`);
    }
  } catch (error) {
    console.error("❌ Erro inesperado:", error);

    // Reverter em caso de erro
    if (inputElement) {
      inputElement.style.backgroundColor = "#f8d7da";
      inputElement.value = `R$${oldValue.toFixed(2)}`;
      inputElement.disabled = false;
    }

    alert(`❌ Erro: ${error.message}`);
  }
};

  // ✅ FUNÇÃO PARA SALVAR OBSERVAÇÃO
  const salvarObservacao = useCallback(async () => {
    if (modalJustIndex === null) return;

    setSalvandoObservacao(true);

    try {
      const globalIndex = (currentPage - 1) * itemsPerPage + modalJustIndex;
      const item = data[globalIndex];
      const novaObservacao = item.justificativa || "";

      // Obter dados do usuário
      const token = localStorage.getItem("authToken");
      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;
      const agora = new Date().toISOString();

      const resultado = await atualizarObservacao(
        item.IdOrcto,
        novaObservacao,
        currentUsername,
        agora
      );

      if (resultado.success) {
        // ✅ SUCESSO - Atualizar estado local
        const newData = [...data];
        newData[globalIndex].obs = novaObservacao;
        setData(newData);

        alert("✅ Observação salva com sucesso!");
        closeJustModal();
      } else {
        alert(`❌ Erro ao salvar observação: ${resultado.message}`);
      }
    } catch (error) {
      console.error("❌ Erro inesperado:", error);
      alert(`❌ Erro: ${error.message}`);
    } finally {
      setSalvandoObservacao(false);
    }
  }, [modalJustIndex, currentPage, itemsPerPage, data, atualizarObservacao]);

  // ✅ NOVA FUNÇÃO PARA SALVAR ALERTA
  const salvarAlerta = useCallback(async () => {
    if (modalIndex === null) return;

    setSalvandoAlerta(true);

    try {
      const globalIndex = (currentPage - 1) * itemsPerPage + modalIndex;
      const item = data[globalIndex];

      // Obter dados do usuário
      const token = localStorage.getItem("authToken");
      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;
      const agora = new Date().toISOString();

      const resultado = await atualizarAlerta(
        item.IdOrcto,
        item.email25 || "",
        item.email50 || "",
        item.email75 || "",
        item.email100 || "",
        currentUsername,
        agora
      );

      if (resultado.success) {
        alert("✅ Alertas salvos com sucesso!");
        closeModal();
      } else {
        alert(`❌ Erro ao salvar alertas: ${resultado.message}`);
      }
    } catch (error) {
      console.error("❌ Erro inesperado:", error);
      alert(`❌ Erro: ${error.message}`);
    } finally {
      setSalvandoAlerta(false);
    }
  }, [modalIndex, currentPage, itemsPerPage, data, atualizarAlerta]);

  // ✅ FUNÇÃO PARA FORMATAR DATAS
  const formatarData = (dataString) => {
    try {
      // Se for uma string no formato ISO (2025-04-16T00:00:00.000Z)
      if (typeof dataString === "string" && dataString.includes("T")) {
        const data = new Date(dataString);
        return data.toLocaleDateString("pt-BR");
      }

      // Se for uma string no formato YYYY-MM-DD
      if (typeof dataString === "string" && dataString.includes("-")) {
        const [ano, mes, dia] = dataString.split("T")[0].split("-");
        return `${dia}/${mes}/${ano}`;
      }

      // Se for um objeto Date
      if (dataString instanceof Date) {
        return dataString.toLocaleDateString("pt-BR");
      }

      // Tentativa genérica
      const data = new Date(dataString);
      if (!isNaN(data.getTime())) {
        return data.toLocaleDateString("pt-BR");
      }

      return "Data inválida";
    } catch (error) {
      console.warn("Erro ao formatar data:", dataString, error);
      return "Data inválida";
    }
  };

  // ✅ FUNÇÃO PARA CARREGAR MOVIMENTOS
  const carregarMovimentos = useCallback(async (centroCusto, natureza, ano) => {
    try {
      setLoadingMovimentos(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // ✅ CONSTRUÇÃO DA WHERE CLAUSE PARA MOVIMENTOS
      let whereClause = `AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL = '${PCODFILIAL}' AND ANO = '${ano}'`;

      if (centroCusto && centroCusto !== "" && centroCusto !== "%") {
        whereClause += ` AND CODCCUSTO = '${centroCusto}'`;
      }

      if (natureza && natureza !== "" && natureza !== "%") {
        whereClause += ` AND CODTBORCAMENTO = '${natureza}'`;
      }

      console.log("🔍 Carregando movimentos com WHERE:", whereClause);

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0019/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            WHERE: whereClause,
            ORDER_BY: "DTORIG, FORNECEDOR",
          }),
        }
      );

      const result = await response.json();
      console.log("📋 Resultado dos movimentos:", result);

      if (result.success && result.data) {
        // ✅ MAPEAR DADOS DOS MOVIMENTOS
        const movimentosMapeados = result.data.map((item) => ({
          idMov: item.IDMOVDEST || item.IDMOVORG || "N/A",
          data: item.DTORIG ? formatarData(item.DTORIG) : "N/A",
          dataOrig: item.DTORIG || null, // data crua, usada para ordenar
          fornecedor: item.FORNECEDOR || "N/A",
          valor: parseFloat(item.VALORPATIO || item.LIQUIDO || 0),
          origem: item.ORIGEM || "N/A",
          destino: item.DESTINO || "N/A",
          tipo: item.TIPO || "N/A",
          status: item.FLANSTATUS || item.TMOVSTATUS || "N/A",
          empenhado: parseFloat(item.EMPENHADO || 0),
          // Dados adicionais para drill-down
          idMovOrg: item.IDMOVORG,
          idMovDest: item.IDMOVDEST,
          codCCusto: item.CODCCUSTO,
          codTbOrcamento: item.CODTBORCAMENTO,
          idContrato: item.IDCONTRATO,
        }));

        setMovimentosData(movimentosMapeados);

        // ✅ Carrega em lote quais documentos cada movimento possui
        if (DOCUMENTOS_HABILITADOS) {
          const idMovs = movimentosMapeados
            .map((m) => m.idMov)
            .filter((id) => id && id !== "N/A");
          carregarDocumentosDisponiveis(idMovs);
        }
      } else {
        console.log("⚠️ Nenhum movimento encontrado");
        setMovimentosData([]);
        setDocumentosDisponiveis({});
      }
    } catch (error) {
      console.error("❌ Erro ao carregar movimentos:", error);
      setMovimentosData([]);
      setDocumentosDisponiveis({});
    } finally {
      setLoadingMovimentos(false);
    }
  }, []);

  // ✅ Busca a disponibilidade de documentos (NF/Boleto/Comprovante) em lote
  const carregarDocumentosDisponiveis = async (idMovs) => {
    setDocumentosDisponiveis({});
    if (!idMovs || idMovs.length === 0) return;

    setLoadingDocs(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}/SQlCustom/DocumentosDisponiveis`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ idMovs }),
        }
      );
      const result = await response.json();
      if (result.success && result.data) {
        setDocumentosDisponiveis(result.data);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar disponibilidade de documentos:", error);
    } finally {
      setLoadingDocs(false);
    }
  };

  // ✅ FUNÇÃO PARA CARREGAR ITENS DO MOVIMENTO
  const carregarItensMovimento = useCallback(async (idMovimento, centroCusto, natureza) => {
    try {
      setLoadingItens(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // ✅ CONSTRUÇÃO DA WHERE CLAUSE PARA ITENS DO MOVIMENTO
      let whereClause = `AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL = '${PCODFILIAL}'`;

      /*if (idMovimento && idMovimento !== "N/A") {
        whereClause += ` AND IDMOVDEST = '${idMovimento}'  AND CODCCUSTO = '${centroCusto}' AND CODTBORCAMENTO = '${natureza}' `;
      }*/

      if (idMovimento && idMovimento !== "N/A") {
        whereClause += ` AND IDMOVDEST = '${idMovimento}'  AND CODCCUSTO = '${centroCusto}' `;
      }

      console.log("🔍 Carregando itens do movimento com WHERE:", whereClause);
      console.log("📦 ID do movimento:", idMovimento);

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0008/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            WHERE: whereClause,
            ORDER_BY: "DESCRICAO, QUANTIDADE",
          }),
        }
      );

      const result = await response.json();
      console.log("📦 Resultado dos itens:", result);

      if (result.success && result.data) {
        // ✅ MAPEAR DADOS DOS ITENS
        const itensMapeados = result.data.map((item) => ({
          idItem: item.IDITEM || item.CODPRD || "N/A",
          descricao: item.DESCRICAO || item.NOMEPRD || "N/A",
          quantidade: parseFloat(item.QUANTIDADE || 0),
          valorUnitario: parseFloat(item.VALORUNITARIO || item.PRECO || 0),
          valorTotal: parseFloat(item.VALORTOTAL || item.VALOR || 0),
          unidade: item.UNIDADE || item.CODUND || "UN",
          observacao: item.OBSERVACAO || item.OBS || "",
          // Referências
          idMovimento: item.IDMOVDEST || item.IDMOVORG,
          numeroNF: item.NUMERONF || item.NRONOTA || "N/A",
          serieNF: item.SERIE || item.SERIE || "N/A",
        }));

        setItensMovimento(itensMapeados);
      } else {
        console.log("⚠️ Nenhum item encontrado para o movimento");
        setItensMovimento([]);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar itens do movimento:", error);
      setItensMovimento([]);
    } finally {
      setLoadingItens(false);
    }
  }, []);

  // 📊 FUNÇÃO PARA CARREGAR TOTAIS (CORRIGIDA PARA TRATAR ARRAY)
  const carregarTotaisOrcamento = useCallback(async (whereClause) => {
    try {
      setLoadingTotais(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      console.log("🔄 Carregando totais com WHERE:", whereClause);
      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");
      const selectedYear = localStorage.getItem("selectedYear");

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0005/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            PARAM: [
              {
                CODFILIAL: PCODFILIAL,
                CODCOLIGADA: PCODCOLIGADA,
                ANO: selectedYear,
              },
            ],

            WHERE: whereClause,
            SELECT: `
              SUM(COALESCE(ORCADO, 0)) as totalOrcado,
              SUM(COALESCE(REALIZADO, 0)) as totalRealizado,
              SUM(COALESCE(ECONOMIA, 0)) as totalEconomia
            `,
            GROUP_BY: "1",
          }),
        }
      );

      const result = await response.json();
      console.log("📊 Resultado dos totais:", result);

      if (result.success && result.data && result.data.length > 0) {
        // 🔧 VERIFICAR SE É UM ÚNICO RESULTADO AGREGADO OU ARRAY
        if (
          result.data.length === 1 &&
          result.data[0].totalOrcado !== undefined
        ) {
          // Resultado já agregado pela API
          const totais = {
            orcado: parseFloat(result.data[0].totalOrcado || 0),
            realizado: parseFloat(result.data[0].totalRealizado || 0),
            economia: parseFloat(result.data[0].totalEconomia || 0),
            empenhado: parseFloat(result.data[0].empenhado || 0),
          };

          totais.Consumo =
            totais.orcado > 0 ? (totais.realizado / totais.orcado) * 100 : 0;
          console.log("📈 Totais da API (agregado):", totais);
          setDataTotal([totais]);
        } else {
          // 🛠️ FALLBACK: SOMAR NO CLIENTE SE API RETORNAR ARRAY
          console.log("⚠️ API retornou array, somando no cliente...");
          const totais = result.data.reduce(
            (acc, item) => ({
              orcado: acc.orcado + parseFloat(item.ORCADO || 0),
              realizado: acc.realizado + parseFloat(item.REALIZADO || 0),
              economia: acc.economia + parseFloat(item.ECONOMIA || 0),
              empenhado: acc.empenhado + parseFloat(item.EMPENHADO || 0),
            }),
            { orcado: 0, realizado: 0, economia: 0, empenhado: 0 }
          );

          totais.Consumo =
            totais.orcado > 0 ? (totais.realizado / totais.orcado) * 100 : 0;
          console.log("📈 Totais calculados no cliente:", totais);
          setDataTotal([totais]);
        }
      } else {
        console.log("⚠️ Nenhum dado de totais retornado");
        setDataTotal([{ orcado: 0, realizado: 0, economia: 0, Consumo: 0 }]);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar totais:", error);
      setDataTotal([{ orcado: 0, realizado: 0, economia: 0, Consumo: 0 }]);
    } finally {
      setLoadingTotais(false);
    }
  }, []);

  // 🔄 FUNÇÃO PARA ATUALIZAR TOTAIS COM useCallback
  const atualizarTotais = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;
      const selectedYear = localStorage.getItem("selectedYear");
      const selectedFilial = localStorage.getItem("selectedFilial");
      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");

      let whereClause = `AND TIPO = 'Despesas' AND selectedFilial ='${selectedFilial}' AND ANO ='${selectedYear}' AND CODUSUARIO='${currentUsername}' 
                         AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL ='${PCODFILIAL}' `;

      if (
        filtros.centroCusto &&
        filtros.centroCusto !== "" &&
        filtros.centroCusto !== "%"
      ) {
        whereClause += ` AND CODCCUSTO = '${filtros.centroCusto}'`;
      }

      if (
        filtros.natureza &&
        filtros.natureza !== "" &&
        filtros.natureza !== "%"
      ) {
        whereClause += ` AND CODTBORCAMENTO = '${filtros.natureza}' `;
      }

      console.log("🎯 Atualizando totais para centro:", filtros.centroCusto);
      console.log("🔍 WHERE clause:", whereClause);

      await carregarTotaisOrcamento(whereClause);
    } catch (error) {
      console.error("❌ Erro ao atualizar totais:", error);
    }
  }, [filtros, carregarTotaisOrcamento]);

  // 🏢 FUNÇÃO PARA CARREGAR OPÇÕES DE CENTRO DE CUSTO (CORRIGIDA)
  const carregarOpcoesCentroCusto = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;
      const selectedYear = localStorage.getItem("selectedYear");
      const selectedFilial = localStorage.getItem("selectedFilial");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0006/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            WHERE: `AND TIPO = 'Despesas' AND selectedFilial ='${selectedFilial}' AND ANO ='${selectedYear}' AND CODUSUARIO='${currentUsername}'  `,
            ORDER_BY: "CENTROCUSTO",
            DISTINCT: "CODCCUSTO, CENTROCUSTO",
          }),
        }
      );

      const result = await response.json();
      if (result.success && result.data) {
        // ✅ REMOÇÃO DE DUPLICATAS NO FRONTEND
        const centrosCustoUnicos = result.data.reduce((acc, item) => {
          const existe = acc.find((cc) => cc.CODCCUSTO === item.CODCCUSTO);
          if (!existe) {
            acc.push(item);
          }
          return acc;
        }, []);

        setOpcoesCentroCusto(centrosCustoUnicos);
      }
    } catch (error) {
      console.error("Erro ao carregar centros de custo:", error);
    }
  };

  // 📋 FUNÇÃO PARA CARREGAR OPÇÕES DE NATUREZA (CORRIGIDA)
  const carregarOpcoesNatureza = async (centroCustoFiltro = null) => {
  try {
    console.log(`🔍 Carregando naturezas com filtro: ${centroCustoFiltro}`);
    
    const token = localStorage.getItem("authToken");
    if (!token) return;

    const tokenPayload = JSON.parse(atob(token.split(".")[1]));
    const currentUsername = tokenPayload.username || tokenPayload.id;
    const selectedYear = localStorage.getItem("selectedYear");
    const selectedFilial = localStorage.getItem("selectedFilial");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    // ✅ CONSTRUIR WHERE CLAUSE COM FILTRO DE CENTRO DE CUSTO
    let whereClause = `AND TIPO = 'Despesas' AND selectedFilial ='${selectedFilial}' AND ANO ='${selectedYear}' AND CODUSUARIO='${currentUsername}'`;

    // ✅ ADICIONAR FILTRO DE CENTRO DE CUSTO SE FORNECIDO
    if (centroCustoFiltro && centroCustoFiltro !== "" && centroCustoFiltro !== "%") {
      whereClause += ` AND CODCCUSTO = '${centroCustoFiltro}'`;
      console.log(`✅ Filtro aplicado para centro: ${centroCustoFiltro}`);
    } else {
      console.log(`📋 Carregando todas as naturezas (sem filtro de centro)`);
    }

    console.log(`🔍 WHERE clause final: ${whereClause}`);

    const response = await fetch(
      `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0006/filter`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          WHERE: whereClause,
          ORDER_BY: "NATUREZA",
          DISTINCT: "CODTBORCAMENTO, NATUREZA",
        }),
      }
    );

    const result = await response.json();
    
    if (result.success && result.data) {
      // ✅ REMOÇÃO DE DUPLICATAS NO FRONTEND
      const naturezasUnicas = result.data.reduce((acc, item) => {
        const existe = acc.find(
          (nat) => nat.CODTBORCAMENTO === item.CODTBORCAMENTO
        );
        if (!existe) {
          acc.push(item);
        }
        return acc;
      }, []);

      console.log(`✅ Naturezas carregadas: ${naturezasUnicas.length} itens`);
      console.log(`📋 Primeiras 3 naturezas:`, naturezasUnicas.slice(0, 3));
      
      setOpcoesNatureza(naturezasUnicas);
    } else {
      console.log("⚠️ Nenhuma natureza encontrada ou erro na API");
      setOpcoesNatureza([]);
    }
  } catch (error) {
    console.error("❌ Erro ao carregar naturezas:", error);
    setOpcoesNatureza([]);
  }
};

  // 💰 FUNÇÃO PRINCIPAL PARA CARREGAR DADOS DO ORÇAMENTO (CORRIGIDA)
  const carregarDadosOrcamento = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const tokenPayload = JSON.parse(atob(token.split(".")[1]));
      const currentUsername = tokenPayload.username || tokenPayload.id;
      const selectedYear = localStorage.getItem("selectedYear");
      const selectedFilial = localStorage.getItem("selectedFilial");
      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // ✅ CONSTRUÇÃO CORRETA DA WHERE CLAUSE
      let whereClause = `AND TIPO = 'Despesas' AND selectedFilial ='${selectedFilial}' AND ANO ='${selectedYear}' AND CODUSUARIO='${currentUsername}'  
                         AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL ='${PCODFILIAL}' `;

      // Adicionar filtros apenas se não estiverem vazios e não forem "%"
      if (
        filtros.centroCusto &&
        filtros.centroCusto !== "" &&
        filtros.centroCusto !== "%"
      ) {
        whereClause += ` AND CODCCUSTO = '${filtros.centroCusto}'`;
      }

      if (
        filtros.natureza &&
        filtros.natureza !== "" &&
        filtros.natureza !== "%"
      ) {
        whereClause += ` AND CODTBORCAMENTO = '${filtros.natureza}'`;
      }

      console.log("WHERE Clause:", whereClause); // Para debug

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0005/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            PARAM: [
              {
                CODFILIAL: PCODFILIAL,
                CODCOLIGADA: PCODCOLIGADA,
                ANO: selectedYear,
              },
            ],
            WHERE: whereClause,
            ORDER_BY: "NATUREZA, CODTBORCAMENTO",
          }),
        }
      );

      const result = await response.json();
      console.log("Resultado da API:", result); // Para debug

      if (result.success && result.data) {
        // ✅ MAPEAR DADOS INCLUINDO ALERTAS DO BANCO
        const dadosMapeados = result.data.map((item) => ({
          IdOrcto: item.IDORCTO,
          codigo: item.CODTBORCAMENTO,
          natureza: item.NATUREZA,
          orcado: parseFloat(item.ORCADO || 0),
          realizado: parseFloat(item.REALIZADO || 0),

          resultado:
            parseFloat(item.ORCADO || 0) -
            (parseFloat(item.REALIZADO || 0) + parseFloat(item.EMPENHADO || 0)),

          economia: parseFloat(item.ECONOMIA || 0),
          empenhado: parseFloat(item.EMPENHADO || 0),
          obs: item.OBS || "",
          justificativa: item.OBS || "",
          centroCusto: item.CENTROCUSTO,
          codCCusto: item.CODCCUSTO,
          // ✅ CARREGAR CAMPOS DE ALERTA DO BANCO DE DADOS
          email25: item.ALERTA25 || "",
          email50: item.ALERTA50 || "",
          email75: item.ALERTA75 || "",
          email100: item.ALERTA100 || "",
        }));

        setData(dadosMapeados);

        // ✅ CORREÇÃO: Passar a whereClause correta para os totais
        await carregarTotaisOrcamento(whereClause);
      }
    } catch (error) {
      console.error("Erro ao carregar dados do orçamento:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NOVA FUNÇÃO verVersoes COMPLETA
  const verVersoes = async (natureza, idOrcto) => {
    try {
      setLoadingVersoes(true);
      setModalVersaoVisible({ natureza, idOrcto });

      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("Token não encontrado");
        return;
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // Buscar histórico de versões usando a query SQL fornecida
      const whereClause = `AND re.IDORCTO = '${idOrcto}'`;

      console.log("🔍 Buscando versões para ID:", idOrcto);

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0009/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            WHERE: whereClause,
            ORDER_BY: "vs.RECCREATEDON DESC",
          }),
        }
      );

      const result = await response.json();

      if (result.success && result.data) {
        // Mapear dados das versões
        const versoesMapeadas = result.data.map((item, index) => ({
          id: index + 1,
          nome: item.NOME || "N/A",
          versao: item.VERSAO || "N/A",
          data: formatarData(item.RECMODIFIEDON),
          dataCompleta: item.RECMODIFIEDON,
          valor: item.VALOR || "",
          idOrcto: item.IDORCTO,
        }));

        setVersoesData(versoesMapeadas);
        console.log("✅ Versões carregadas:", versoesMapeadas.length);
      } else {
        console.log("⚠️ Nenhuma versão encontrada");
        setVersoesData([]);
      }
    } catch (error) {
      console.error("❌ Erro ao carregar versões:", error);
      alert(`Erro ao carregar histórico de versões: ${error.message}`);
      setVersoesData([]);
    } finally {
      setLoadingVersoes(false);
    }
  };

  // ✅ FUNÇÃO PARA FECHAR O MODAL DE VERSÕES
  const closeVersaoModal = () => {
    setModalVersaoVisible(null);
    setVersoesData([]);
    setVersaoDetalhada(null);
  };

  // ✅ FUNÇÃO PARA VER DETALHES DE UMA VERSÃO
  const verDetalhesVersao = (versao) => {
    setVersaoDetalhada(versao);
  };

  // useEffect para carregar dados iniciais
  useEffect(() => {
    carregarOpcoesCentroCusto();
    carregarOpcoesNatureza();
    atualizarTotais();
  }, [atualizarTotais]);

  // useEffect para atualizar totais quando filtros mudarem
  useEffect(() => {
    atualizarTotais();
  }, [filtros.centroCusto, filtros.natureza, atualizarTotais]);

  // useEffect para recarregar dados quando filtros mudarem
  useEffect(() => {
    if (filtros.centroCusto) {
      carregarDadosOrcamento();
    }
  }, [filtros.centroCusto, filtros.natureza]);

  // 💰 CÁLCULOS DOS TOTAIS GLOBAIS (COM FALLBACK SEGURO)
const consumoPct =
  dataTotal.length > 0 && dataTotal[0]
    ? Math.round(dataTotal[0].Consumo || 0)   // ← Manter Math.round para porcentagem
    : 0;

const OrcadoGlobal =
  dataTotal.length > 0 && dataTotal[0]
    ? parseFloat(dataTotal[0].orcado || 0)
    : 0;

const RealizadoGlobal =
  dataTotal.length > 0 && dataTotal[0]
    ? parseFloat(dataTotal[0].realizado || 0)
    : 0;

const Empenhado =
  dataTotal.length > 0 && dataTotal[0]
    ? parseFloat(dataTotal[0].empenhado || 0)
    : 0;


const handleFiltroChange = (campo, valor) => {
  console.log(`🔧 Alterando filtro ${campo} para:`, valor);

  // ✅ ATUALIZAR FILTROS
  setFiltros((prev) => ({
    ...prev,
    [campo]: valor,
    // ✅ LIMPAR NATUREZA QUANDO MUDAR CENTRO DE CUSTO
    ...(campo === "centroCusto" && { natureza: "" }),
  }));

  // ✅ RECARREGAR NATUREZAS SE MUDOU CENTRO DE CUSTO
  if (campo === "centroCusto") {
    console.log(`📋 Recarregando naturezas para centro: ${valor}`);
    // ✅ USAR setTimeout PARA GARANTIR QUE O STATE SEJA ATUALIZADO PRIMEIRO
    setTimeout(() => {
      carregarOpcoesNatureza(valor);
    }, 100);
  }

  setCurrentPage(1);
};

  const openModal = (index) => setModalIndex(index);
  const closeModal = () => setModalIndex(null);
  const openJustModal = (index) => setModalJustIndex(index);
  const closeJustModal = () => setModalJustIndex(null);

  // ✅ FUNÇÃO ATUALIZADA PARA ABRIR MODAL DE MOVIMENTOS
  const openListModal = async (index) => {
    setModalListVisible(index);
    resetarModalMovimentos(); // cada natureza abre sem filtro e por data asc
    const item = paginatedData[index];
    const selectedYear = localStorage.getItem("selectedYear");

    // ✅ CARREGAR MOVIMENTOS COM OS PARÂMETROS CORRETOS
    await carregarMovimentos(
      item.codCCusto, // Centro de custo do item selecionado
      item.codigo, // Código da natureza orçamentária
      selectedYear // Ano selecionado
    );
  };

  const closeListModal = () => {
    setModalListVisible(null);
    setMovimentosData([]); // Limpar dados ao fechar
    resetarModalMovimentos(); // Limpar filtros e ordenação ao fechar
  };

  // ✅ FUNÇÕES PARA O MODAL DE ITENS
  const openItensModal = async (movimento) => {
    setModalItensVisible(movimento);
    
    // Pega os dados do item que está sendo visualizado na modal de movimentos
    const itemAtual = paginatedData[modalListVisible];
    
    await carregarItensMovimento(
      movimento.idMov, 
      itemAtual.codCCusto,  // centro de custo do item
      itemAtual.codigo      // código da natureza
    );
  };

  const closeItensModal = () => {
    setModalItensVisible(null);
    setItensMovimento([]); // Limpar dados ao fechar
  };

  // ✅ FUNÇÕES PARA O MODAL DE COMPROVANTE (GED - Itaú)
  const abrirComprovanteModal = async (movimento) => {
    setModalComprovanteVisible(true);
    setComprovantesData([]);
    setComprovanteSelecionado(0);

    // Sem IDMOV válido não há como vincular ao GED
    if (!movimento.idMov || movimento.idMov === "N/A") {
      return;
    }

    setLoadingComprovante(true);
    try {
      const token = localStorage.getItem("authToken");
      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");

      const response = await fetch(
        `${API_BASE_URL}/SQlCustom/ComprovanteItau`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            idMov: movimento.idMov,
            codColigada: PCODCOLIGADA,
          }),
        }
      );

      const result = await response.json();
      if (result.success && result.data?.length > 0) {
        setComprovantesData(result.data);
      }
    } catch (error) {
      console.error("❌ Erro ao buscar comprovante:", error);
    } finally {
      setLoadingComprovante(false);
    }
  };

  const closeComprovanteModal = () => {
    setModalComprovanteVisible(false);
    setComprovantesData([]);
    setComprovanteSelecionado(0);
  };

  // ✅ FUNÇÕES PARA O MODAL DE BOLETO (Qive)
  const carregarBoletoPdf = async (boletoId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}/SQlCustom/BoletoQivePdf/${boletoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        setBoletoPdfUrl(null);
        return;
      }
      const blob = await response.blob();
      setBoletoPdfUrl((urlAnterior) => {
        if (urlAnterior) URL.revokeObjectURL(urlAnterior);
        return URL.createObjectURL(blob);
      });
    } catch (error) {
      console.error("❌ Erro ao carregar PDF do boleto:", error);
      setBoletoPdfUrl(null);
    }
  };

  const abrirBoletoModal = async (movimento) => {
    setModalBoletoVisible(true);
    setBoletosData([]);
    setBoletoSelecionado(0);
    setBoletoPdfUrl(null);

    // Sem IDMOV válido não há como vincular ao boleto
    if (!movimento.idMov || movimento.idMov === "N/A") {
      return;
    }

    setLoadingBoleto(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/SQlCustom/BoletoQive`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ idMov: movimento.idMov }),
      });

      const result = await response.json();
      if (result.success && result.data?.length > 0) {
        setBoletosData(result.data);
        if (result.data[0].TemPdf) {
          await carregarBoletoPdf(result.data[0].Id);
        }
      }
    } catch (error) {
      console.error("❌ Erro ao buscar boletos:", error);
    } finally {
      setLoadingBoleto(false);
    }
  };

  const selecionarBoleto = async (indice) => {
    setBoletoSelecionado(indice);
    setBoletoPdfUrl((urlAnterior) => {
      if (urlAnterior) URL.revokeObjectURL(urlAnterior);
      return null;
    });
    const boleto = boletosData[indice];
    if (boleto?.TemPdf) {
      await carregarBoletoPdf(boleto.Id);
    }
  };

  const closeBoletoModal = () => {
    setModalBoletoVisible(false);
    setBoletosData([]);
    setBoletoSelecionado(0);
    setBoletoPdfUrl((urlAnterior) => {
      if (urlAnterior) URL.revokeObjectURL(urlAnterior);
      return null;
    });
  };

  // ✅ FUNÇÕES PARA O MODAL DE CONTRATO (documento assinado)
  // Puxa o PDF do contrato vinculado ao movimento (via IDMOV + CODCOLIGADA do contexto).
  const abrirContratoModal = async (movimento) => {
    setModalContratoVisible(true);
    setContratoErro(null);
    setContratoMovAtual(movimento);
    setContratoPdfUrl((urlAnterior) => {
      if (urlAnterior) URL.revokeObjectURL(urlAnterior);
      return null;
    });

    if (!movimento.idMov || movimento.idMov === "N/A") {
      setContratoErro("Movimento sem IDMOV — não é possível localizar o contrato.");
      return;
    }

    setLoadingContrato(true);
    try {
      const token = localStorage.getItem("authToken");
      const codColigada = localStorage.getItem("PCODCOLIGADA");
      const response = await fetch(`${API_BASE_URL}/SQlCustom/ContratoDoc`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ idMov: movimento.idMov, codColigada }),
      });

      if (!response.ok) {
        // 404 = contrato sem documento assinado; outros = erro genérico
        setContratoErro(
          response.status === 404
            ? "Este contrato não possui documento assinado armazenado."
            : "Não foi possível carregar o documento do contrato."
        );
        return;
      }

      const blob = await response.blob();
      setContratoPdfUrl(URL.createObjectURL(blob));
    } catch (error) {
      console.error("❌ Erro ao carregar contrato:", error);
      setContratoErro("Não foi possível carregar o documento do contrato.");
    } finally {
      setLoadingContrato(false);
    }
  };

  const closeContratoModal = () => {
    setModalContratoVisible(false);
    setContratoErro(null);
    setContratoMovAtual(null);
    setContratoPdfUrl((urlAnterior) => {
      if (urlAnterior) URL.revokeObjectURL(urlAnterior);
      return null;
    });
  };

  // ✅ FUNÇÕES PARA O MODAL DE NOTA FISCAL (Qive)
  const carregarNfPdf = async (nfId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}/SQlCustom/NotaFiscalQivePdf/${nfId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        setNfPdfUrl(null);
        return;
      }
      const blob = await response.blob();
      setNfPdfUrl((urlAnterior) => {
        if (urlAnterior) URL.revokeObjectURL(urlAnterior);
        return URL.createObjectURL(blob);
      });
    } catch (error) {
      console.error("❌ Erro ao carregar PDF da NF:", error);
      setNfPdfUrl(null);
    }
  };

  const abrirNfModal = async (movimento) => {
    setModalNfVisible(true);
    setNotasFiscaisData([]);
    setNfSelecionada(0);
    setNfPdfUrl(null);

    // Sem IDMOV válido não há como vincular à NF
    if (!movimento.idMov || movimento.idMov === "N/A") {
      return;
    }

    setLoadingNf(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/SQlCustom/NotaFiscalQive`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ idMov: movimento.idMov }),
      });

      const result = await response.json();
      if (result.success && result.data?.length > 0) {
        setNotasFiscaisData(result.data);
        if (result.data[0].TemPdf) {
          await carregarNfPdf(result.data[0].Id);
        }
      }
    } catch (error) {
      console.error("❌ Erro ao buscar nota fiscal:", error);
    } finally {
      setLoadingNf(false);
    }
  };

  const selecionarNf = async (indice) => {
    setNfSelecionada(indice);
    setNfPdfUrl((urlAnterior) => {
      if (urlAnterior) URL.revokeObjectURL(urlAnterior);
      return null;
    });
    const nf = notasFiscaisData[indice];
    if (nf?.TemPdf) {
      await carregarNfPdf(nf.Id);
    }
  };

  // ✅ Renderiza um botão de documento com estado (disponível = preenchido,
  //    indisponível = desabilitado + tooltip)
  const renderBotaoDocumento = (movimento, tipo, emoji, label, onClick) => {
    const disp = documentosDisponiveis[movimento.idMov];
    const qtd = disp ? disp[tipo] : undefined;
    const temIdMov = movimento.idMov && movimento.idMov !== "N/A";
    const carregando = !disp && loadingDocs;
    const disponivel = qtd > 0;

    let className = "btn btn-sm ";
    let disabled = false;
    let title = label;

    if (!temIdMov) {
      className += "btn-outline-secondary disabled";
      disabled = true;
      title = "Movimento sem identificação para vincular documentos";
    } else if (carregando) {
      className += "btn-outline-secondary disabled";
      disabled = true;
      title = "Verificando documentos...";
    } else if (disponivel) {
      className += "btn-primary";
      title = qtd > 1 ? `${label} (${qtd})` : label;
    } else {
      className += "btn-outline-secondary disabled";
      disabled = true;
      title = `${label}: não disponível ainda para este movimento`;
    }

    return (
      <button
        type="button"
        className={className}
        style={{ whiteSpace: "nowrap" }}
        title={title}
        disabled={disabled}
        onClick={disabled ? undefined : () => onClick(movimento)}
      >
        {emoji} {label}
      </button>
    );
  };

  const baixarNfXml = async (nf) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}/SQlCustom/NotaFiscalQiveXml/${nf.Id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) return;
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `nf_${nf.NumeroNota || nf.Id}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("❌ Erro ao baixar XML da NF:", error);
    }
  };

  const closeNfModal = () => {
    setModalNfVisible(false);
    setNotasFiscaisData([]);
    setNfSelecionada(0);
    setNfPdfUrl((urlAnterior) => {
      if (urlAnterior) URL.revokeObjectURL(urlAnterior);
      return null;
    });
  };

  const handleModalChange = (field, value) => {
    const globalIndex = (currentPage - 1) * itemsPerPage + modalIndex;
    const newData = [...data];
    newData[globalIndex][field] = value;
    setData(newData);
  };

  const handleJustChange = (value) => {
    const globalIndex = (currentPage - 1) * itemsPerPage + modalJustIndex;
    const newData = [...data];
    newData[globalIndex].justificativa = value;
    setData(newData);
  };


  // ✅ FUNÇÃO PARA CARREGAR DADOS DO ANO ANTERIOR
  const carregarDadosAnoAnterior = useCallback(async (item) => {
    try {
      setLoadingAnoAnterior(true);
      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("Token não encontrado");
        return;
      }

      const selectedYear = localStorage.getItem("selectedYear");
      const anoAnterior = parseInt(selectedYear) - 1;
      const PCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
      const PCODFILIAL = localStorage.getItem("PCODFILIAL");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      // ✅ CONSTRUÇÃO DA WHERE CLAUSE PARA ANO ANTERIOR
      let whereClause = `AND ANO = '${anoAnterior}' AND CODCOLIGADA = '${PCODCOLIGADA}' AND CODFILIAL = '${PCODFILIAL}'`;

      // Filtrar pelo mesmo centro de custo e natureza do item atual
      if (item.codCCusto) {
        whereClause += ` AND CODCCUSTO = '${item.codCCusto}'`;
      }

      if (item.codigo) {
        whereClause += ` AND CODTBORCAMENTO = '${item.codigo}'`;
      }

      console.log("🔍 Carregando dados do ano anterior:", anoAnterior);
      console.log("🔍 WHERE clause:", whereClause);

      const response = await fetch(
        `${API_BASE_URL}/totvs-sql/sql_MAN_ORC_0005/filter`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            PARAM: [
              {
                CODFILIAL: PCODFILIAL,
                CODCOLIGADA: PCODCOLIGADA,
                ANO: anoAnterior,
              },
            ],
            WHERE: whereClause,
          }),
        }
      );

      const result = await response.json();
      console.log("📊 Resultado do ano anterior:", result);

      if (result.success && result.data && result.data.length > 0) {
        // ✅ MAPEAR DADOS DO ANO ANTERIOR
        const dadosMapeados = result.data[0]; // Pegar o primeiro registro

        const dadosFormatados = {
          ano: anoAnterior,
          natureza: dadosMapeados.NATUREZA || item.natureza,
          codigo: dadosMapeados.CODTBORCAMENTO || item.codigo,
          centroCusto: dadosMapeados.CENTROCUSTO || item.centroCusto,
          orcado: parseFloat(dadosMapeados.ORCADO || 0),
          realizado: parseFloat(dadosMapeados.REALIZADO || 0),
          economia: parseFloat(dadosMapeados.ECONOMIA || 0),
          eficiente: (
            (parseFloat(dadosMapeados.REALIZADO || 0) /
              parseFloat(dadosMapeados.ORCADO || 0)) *
            100
          ).toFixed(2),
          // Cálculos adicionais
          consumoPercent:
            dadosMapeados.ORCADO > 0
              ? Math.round(
                  (parseFloat(dadosMapeados.REALIZADO || 0) /
                    parseFloat(dadosMapeados.ORCADO || 0)) *
                    100
                )
              : 0,
          saldo:
            parseFloat(dadosMapeados.ORCADO || 0) -
            parseFloat(dadosMapeados.REALIZADO || 0),
        };

        setDadosAnoAnterior(dadosFormatados);
      } else {
        console.log("⚠️ Nenhum dado encontrado para o ano anterior");
        setDadosAnoAnterior({
          ano: anoAnterior,
          natureza: item.natureza,
          codigo: item.codigo,
          centroCusto: item.centroCusto,
          orcado: 0,
          realizado: 0,
          economia: 0,
          eficiente: 0,
          consumoPercent: 0,
          saldo: 0,
          semDados: true,
        });
      }
    } catch (error) {
      console.error("❌ Erro ao carregar dados do ano anterior:", error);
      alert(`Erro ao carregar dados do ano anterior: ${error.message}`);
      setDadosAnoAnterior(null);
    } finally {
      setLoadingAnoAnterior(false);
    }
  }, []);

  // ✅ FUNÇÃO PARA ABRIR O MODAL DO ANO ANTERIOR
  const abrirModalAnoAnterior = async (index) => {
    const item = paginatedData[index];
    setModalAnoAnteriorVisible(index);
    await carregarDadosAnoAnterior(item);
  };

  // ✅ FUNÇÃO PARA FECHAR O MODAL DO ANO ANTERIOR
  const fecharModalAnoAnterior = () => {
    setModalAnoAnteriorVisible(null);
    setDadosAnoAnterior(null);
  };

  return (
    <div className="container-fluid py-2 px-4" style={{ minHeight: "100vh" }}>
      <div
        className="bg-white rounded shadow p-4"
        style={{ backgroundColor: "#ffffffb3" }}
      >
        {/* TÍTULO */}
        <h5
          className="text-dark fw-semibold mb-4"
          style={{ fontSize: "1.50rem" }}
        >
          💼 Painel de <b className="text-danger ">Despesas</b> Gerais —{" "}
          <span className="text-dark fw-semibold mb-4">
            ✅ Ano {localStorage.getItem("selectedYear")} -{" "}
            {localStorage.getItem("NomeFilial")}
            {/* ✅ INDICADOR DE STATUS DE EDIÇÃO */}
            {!podeEditarOrcamento() && (
              <span className="badge bg-warning text-dark ms-2">
                🔒 Somente Leitura
              </span>
            )}
            {podeEditarOrcamento() && (
              <span className="badge bg-success ms-2">✏️ Editável</span>
            )}
          </span>
          {filtros.centroCusto && filtros.centroCusto !== "%" && (
            <span className="badge bg-info ms-2">
              Centro: {filtros.centroCusto}
            </span>
          )}
        </h5>

        {!podeEditarOrcamento() && (
          <div
            className="alert alert-warning d-flex align-items-center mb-3"
            role="alert"
          >
            <div className="me-2" style={{ fontSize: "1.5rem" }}>
              🔒
            </div>
            <div>
              <strong>Modo Somente Leitura</strong>
              <br />
              <small>
                Os valores orçamentários não podem ser editados para o ano{" "}
                <strong>{localStorage.getItem("selectedYear")}</strong>. Apenas
                anos futuros (2026 em diante) permitem alterações.
              </small>
            </div>
          </div>
        )}



        
        {/* FILTROS PRINCIPAIS */}
      <div className="row g-3 mb-3">
  {/* Centro de Custo */}
  <div className="col-md-4">
    <label className="form-label fw-medium">Centro de Custo</label>
    <select
      className="form-select shadow-sm"
      value={filtros.centroCusto}
      onChange={(e) =>
        handleFiltroChange("centroCusto", e.target.value)
      }
    >
      <option value="">Selecione um Centro de Custo</option>
      {opcoesCentroCusto.map((cc, index) => (
        <option key={`${cc.CODCCUSTO}-${index}`} value={cc.CODCCUSTO}>
          {cc.CODCCUSTO} - {cc.CENTROCUSTO}
        </option>
      ))}
    </select>
  </div>

  {/* ✅ NATUREZA ORÇAMENTÁRIA CORRIGIDA */}
  <div className="col-md-4">
    <label className="form-label fw-medium">
      Natureza Orçamentária
      {/* ✅ INDICADOR VISUAL QUANDO ESTÁ FILTRADO */}
      {filtros.centroCusto && filtros.centroCusto !== "" && (
        <small className="text-success ms-2">
          (filtrado por: {filtros.centroCusto})
        </small>
      )}
    </label>

    <select
      className="form-select shadow-sm"
      value={filtros.natureza}
      onChange={(e) => handleFiltroChange("natureza", e.target.value)}
    >
      {/* ✅ OPÇÃO DINÂMICA BASEADA NO FILTRO */}
      <option value="">
        {filtros.centroCusto && filtros.centroCusto !== ""
          ? `Todas do Centro ${filtros.centroCusto}`
          : "Todas as Naturezas"}
      </option>

      {/* ✅ MAPEAR NATUREZAS (já filtradas pela função carregarOpcoesNatureza) */}
      {opcoesNatureza.map((nat, index) => (
        <option
          key={`${nat.CODTBORCAMENTO}-${index}`}
          value={nat.CODTBORCAMENTO}
        >
          {nat.CODTBORCAMENTO} - {nat.NATUREZA}
        </option>
      ))}
    </select>

    {/* ✅ FEEDBACK QUANDO NÃO HÁ NATUREZAS PARA O CENTRO */}
    {filtros.centroCusto &&
      filtros.centroCusto !== "" &&
      opcoesNatureza.length === 0 && (
        <small className="text-warning mt-1 d-block">
          ⚠️ Nenhuma natureza encontrada para este centro de custo
        </small>
      )}

    {/* ✅ CONTADOR DE NATUREZAS DISPONÍVEIS */}
    {opcoesNatureza.length > 0 && (
      <small className="text-muted mt-1 d-block">
        {opcoesNatureza.length} natureza(s) disponível(eis)
        {filtros.centroCusto &&
          filtros.centroCusto !== "" &&
          " para este centro"}
      </small>
    )}
  </div>

  {/* ✅ ADICIONAR TERCEIRA COLUNA PARA EQUILIBRAR O LAYOUT */}
  <div className="col-md-4">
    <label className="form-label fw-medium">Ações Rápidas</label>
    <div className="d-flex gap-2">
      <button 
        className="btn btn-outline-primary btn-sm flex-fill"
        onClick={() => {
          setFiltros(prev => ({ ...prev, centroCusto: "", natureza: "" }));
          carregarOpcoesNatureza(); // Recarregar todas as naturezas
        }}
      >
        🔄 Limpar Filtros
      </button>
      <button 
        className="btn btn-outline-success btn-sm flex-fill"
        onClick={carregarDadosOrcamento}
      >
        📊 Atualizar
      </button>
    </div>
  </div>
</div>
        {/* CONTROLES E RESUMOS */}
        <div className="row g-3 mb-4">
          {/* Total Geral */}
          <div className="col-md-2">
            <label className="form-label fw-medium">
              💰 Total Orçado
              <small className="text-muted">
                (
                {loadingTotais
                  ? "⏳ Carregando..."
                  : dataTotal.length > 0
                  ? "✅ Carregado"
                  : "📝 Aguardando"}
                )
              </small>
            </label>
            <input
              type="text"
              className={`form-control shadow-sm ${
                loadingTotais
                  ? "border-warning"
                  : OrcadoGlobal > 0
                  ? "border-success"
                  : "border-secondary"
              }`}
              value={
                loadingTotais
                  ? "Carregando..."
                  : OrcadoGlobal.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })
              }
              readOnly
            />
          </div>

          {/* Empenhado */}
          <div className="col-md-2">
            <label className="form-label fw-medium text-black">
              ⚠️ Empenhado
            </label>
            <input
              type="text"
              className={`form-control shadow-sm ${
                loadingTotais
                  ? "border-warning"
                  : Empenhado > 0
                  ? "border-warning"
                  : "border-secondary"
              }`}
              value={
                loadingTotais
                  ? "Carregando..."
                  : Empenhado.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })
              }
              readOnly
            />
          </div>

          {/* Realizado */}
          <div className="col-md-2">
            <label className="form-label fw-medium text-danger">
              📊 Realizado
            </label>
            <input
              type="text"
              className={`form-control shadow-sm ${
                loadingTotais
                  ? "border-warning"
                  : RealizadoGlobal > 0
                  ? "border-danger"
                  : "border-secondary"
              }`}
              value={
                loadingTotais
                  ? "Carregando..."
                  : RealizadoGlobal.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })
              }
              readOnly
            />
          </div>

          {/* Consumo */}
          <div className="col-md-3">
            <label className="form-label fw-medium text-danger">
              📈 Consumo
              <small className="text-muted">({consumoPct}%)</small>
            </label>
            <div className="progress" style={{ height: "2.4rem" }}>
              {loadingTotais ? (
                <div
                  className="progress-bar bg-warning progress-bar-striped progress-bar-animated"
                  style={{ width: "100%" }}
                >
                  <span className="fw-bold text-dark">Carregando...</span>
                </div>
              ) : (
                <div
                  className={`progress-bar ${
                    consumoPct > 80
                      ? "bg-danger"
                      : consumoPct > 50
                      ? "bg-warning"
                      : "bg-success"
                  }`}
                  role="progressbar"
                  style={{ width: `${Math.min(100, consumoPct)}%` }}
                  aria-valuenow={consumoPct}
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {`${consumoPct}%`}
                </div>
              )}
            </div>
          </div>

          {/* Relatórios 
          <div className="col-md-3">
            <label className="form-label fw-medium text-danger">
              Relatórios
            </label>
            <div className="mt-1">
              <button
                className="btn btn-outline-secondary btn-sm me-2"
                onClick={() => navigate("/lancamentos/rel-Analitico")}
              >
                🆚 Analítico
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => navigate("/lancamentos/rel-sinteico")}
              >
                📝 Sintético
              </button>
            </div>
          </div>
        </div>
      */}

          {/* Relatórios 
          
          const PpCODCOLIGADA = localStorage.getItem("PCODCOLIGADA");
          const PpCODFILIAL = localStorage.getItem("PCODFILIAL");
          const PpANO = localStorage.getItem("selectedYear");
          const PpTIPO = "Despesas";
          const PpCODUSUARIO = localStorage.getItem("username");

          */}
          <div className="col-md-3">
            <label className="form-label fw-medium text-danger">
              Relatórios
            </label>
            <div className="mt-1">
              <BotaoRelatorio
                tipo="lancamentos-analitico"
                texto="Analítico"
                icone="🆚"
                formato="pdf"
                parametros={{
                  pCODCOLIGADA: PpCODCOLIGADA, // use seus estados atuais
                  pCODFILIAL: PpCODFILIAL,
                  pANO: PpANO,
                  pTIPO: PpTIPO,
                  pCODUSUARIO: PpCODUSUARIO,
                }}
                abrirEmModal={true}
                onStartGeneration={iniciarGeracaoRelatorio}
                onFinishGeneration={finalizarGeracaoRelatorio}
              />

              <BotaoRelatorio
                tipo="lancamentos-sintetico"
                texto="Sintético"
                icone="📝"
                formato="pdf"
                parametros={{
                  pCODCOLIGADA: PpCODCOLIGADA, // use seus estados atuais
                  pCODFILIAL: PpCODFILIAL,
                  pANO: PpANO,
                  pTIPO: PpTIPO,
                  pCODUSUARIO: PpCODUSUARIO,
                }}
                abrirEmModal={true}
                onStartGeneration={iniciarGeracaoRelatorio}
                onFinishGeneration={finalizarGeracaoRelatorio}
              />
            </div>
          </div>
        </div>
        {/* LOADING */}
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        )}
        {/* ÁREA ROLÁVEL DA TABELA */}
        <div
          className="table-responsive rounded"
          style={{
            maxHeight: "67vh",
            overflowY: "auto",
          }}
        >
          <table className="table table-striped table-hover align-middle mb-0">
            <thead
              className="table-light text-dark"
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: "#ffffff",
              }}
            >
              <tr>
                <th className="small" hidden>
                  Código
                </th>
                <th className="small">Natureza Orçamentária</th>
                <th className="small">Orçado (Editar)</th>
                <th className="small">Realizado</th>
                <th className="small">Empenhado</th>
                <th className="small">Saldo</th>
                <th className="small">Consumo</th>
                <th className="small">NF</th>
                <th className="small">Obs</th>
                <th className="small">Alerta</th>
                <th className="small">Versão</th>
                <th className="small">
                  {localStorage.getItem("selectedYear") - 1}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => {
                const consumoPercent =
                  item.orcado > 0
                    ? Math.min(
                        100,
                        Math.round((item.realizado / item.orcado) * 100)
                      )
                    : 0;
                return (
                  <tr key={`${item.IdOrcto}-${index}`}>
                    <td hidden>
                      <span className="text-muted small">{item.IdOrcto}</span>
                    </td>
                    <td>
                      <div>
                        <strong>{item.natureza}</strong>
                        <br />
                        <small className="text-muted">{item.codigo}</small>
                      </div>
                    </td>

<td className="position-relative">
  <input
    style={{
      width: "130px",
      padding: "2px 6px",
      backgroundColor: !podeEditarOrcamento() ? "#f8f9fa" : "white",
      cursor: !podeEditarOrcamento() ? "not-allowed" : "text",
    }}
    type="text"
    className={`form-control form-control-sm ${
      !podeEditarOrcamento() ? "text-muted" : ""
    }`}
              defaultValue={`${item.orcado.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          })}`}
    
    onFocus={(e) => {
      if (!podeEditarOrcamento()) return;
      
      // ✅ ARMAZENAR VALOR ORIGINAL LIMPO (não o formatado)
      const valorOriginalLimpo = item.orcado; // Usar direto do state, não do input
      e.target.setAttribute('data-valor-original-numerico', valorOriginalLimpo.toString());
      e.target.setAttribute('data-valor-original-formatado', e.target.value);
      
      console.log('🎯 onFocus - Valores armazenados:', {
        valorOriginalNumerico: valorOriginalLimpo,
        valorOriginalFormatado: e.target.value,
        itemOrcado: item.orcado
      });
      
      // Simplificar para edição (mostrar só o número)
      e.target.value = valorOriginalLimpo.toString().replace('.', ',');
      setTimeout(() => e.target.select(), 0);
      
      // Visual feedback
      e.target.style.backgroundColor = "#fff7e6";
      e.target.style.borderColor = "#ffa940";
    }}
    
    onBlur={(e) => {
      if (!podeEditarOrcamento()) return;
      
      const valorOriginalNumerico = parseFloat(
        e.target.getAttribute('data-valor-original-numerico') || '0'
      );
      const valorOriginalFormatado = e.target.getAttribute('data-valor-original-formatado') || '';
      
      // ✅ FUNÇÃO ÚNICA DE PARSING
      const parseValor = (valor) => {
        if (!valor || valor === '') return 0;
        
        // Converter para string e limpar
        let limpo = valor.toString().trim();
        
        // Remover R$, espaços e outros caracteres
        limpo = limpo.replace(/[R$\s]/g, '');
        
        // Se tem vírgula, formato brasileiro (1.500,50 ou 1500,50)
        if (limpo.includes(',')) {
          // Remove pontos (separadores de milhares) e troca vírgula por ponto
          limpo = limpo.replace(/\./g, '').replace(',', '.');
        }
        
        const numero = parseFloat(limpo);
        const resultado = isNaN(numero) ? 0 : numero;
        
        console.log('🔍 Parse valor:', { entrada: valor, limpo, numero, resultado });
        return resultado;
      };
      
      const valorAtualNumerico = parseValor(e.target.value);
      
      console.log('📊 onBlur - Comparação:', {
        valorOriginalNumerico,
        valorAtualNumerico,
        diferenca: Math.abs(valorAtualNumerico - valorOriginalNumerico),
        threshold: 0.01,
        shouldUpdate: Math.abs(valorAtualNumerico - valorOriginalNumerico) >= 0.01
      });
      
      // ✅ SÓ PROCESSAR SE HOUVE MUDANÇA SIGNIFICATIVA
      if (Math.abs(valorAtualNumerico - valorOriginalNumerico) >= 0.01) {
        console.log('✅ Mudança detectada, chamando handleOrcadoChange');
        handleOrcadoChange(index, valorAtualNumerico); // Passar número direto
      } else {
        console.log('📝 Sem mudanças, restaurando formato original');
        e.target.value = valorOriginalFormatado;
      }
      
      // Reset visual
      e.target.style.backgroundColor = "";
      e.target.style.borderColor = "";
    }}
    
    onKeyDown={(e) => {
      if (!podeEditarOrcamento()) return;
      
      // Enter para confirmar
      if (e.key === 'Enter') {
        e.target.blur();
      }
      
      // Escape para cancelar
      if (e.key === 'Escape') {
        const valorOriginal = e.target.getAttribute('data-valor-original-formatado');
        e.target.value = valorOriginal || `${item.orcado.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}`;
        e.target.blur();
      }
      
      // Permitir teclas de controle
      const permitidas = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End'
      ];
      
      if (permitidas.includes(e.key)) return;
      
      // Permitir vírgula e ponto, mas só um de cada
      if (e.key === ',' || e.key === '.') {
        if (e.target.value.includes(',') || e.target.value.includes('.')) {
          e.preventDefault();
        }
        return;
      }
      
      // Só permitir dígitos
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    }}
    
    disabled={!podeEditarOrcamento()}
    title={
      !podeEditarOrcamento()
        ? `🔒 Edição bloqueada para o ano ${localStorage.getItem("selectedYear")}. Apenas anos futuros (2026+) podem ser editados.`
        : "Clique para editar (Enter=salvar, Esc=cancelar)"
    }
    placeholder={!podeEditarOrcamento() ? "Bloqueado" : "Digite o valor"}
  />
  
  {!podeEditarOrcamento() && (
    <div
      className="position-absolute"
      style={{
        top: "50%",
        right: "5px",
        transform: "translateY(-50%)",
        fontSize: "12px",
        color: "#6c757d",
        pointerEvents: "none"
      }}
    >
      🔒
    </div>
  )}
</td>
                    <td>
                      <span className="badge bg-info text-dark ">
                        {item.realizado.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-warning-subtle text-dark shadow-sm">
                        {item.empenhado.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          item.resultado >= 0
                            ? "bg-success-subtle"
                            : "bg-danger-subtle"
                        } text-dark shadow-sm`}
                      >
                        {item.resultado.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </span>
                    </td>
                    <td style={{ minWidth: "10rem" }}>
                      <div className="progress" style={{ height: "1.2rem" }}>
                        <div
                          className={`progress-bar ${
                            consumoPercent > 80
                              ? "bg-danger"
                              : consumoPercent > 50
                              ? "bg-warning"
                              : "bg-success"
                          }`}
                          role="progressbar"
                          style={{ width: `${consumoPercent}%` }}
                          aria-valuenow={consumoPercent}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        >
                          {`${consumoPercent}%`}
                        </div>
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-dark btn-sm"
                        onClick={() => openListModal(index)}
                      >
                        🔍
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-dark btn-sm"
                        onClick={() => openJustModal(index)}
                      >
                        📋
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-warning btn-sm"
                        onClick={() => openModal(index)}
                      >
                        ⚙️
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => verVersoes(item.natureza, item.IdOrcto)}
                      >
                        🕓
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline-info btn-sm"
                        onClick={() => abrirModalAnoAnterior(index)}
                        title="Ver dados do ano anterior"
                      >
                        📅
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* MODAIS */}
        {/* ✅ MODAL DE ALERTA ATUALIZADO COM BOTÃO SALVAR */}
        {modalIndex !== null && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">🔔 Alerta de Consumo</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                  ></button>
                </div>
                <div className="modal-body">
                  {[25, 50, 75, 100].map((percent) => (
                    <div className="mb-3" key={percent}>
                      <label className="form-label">
                        Email para {percent}%
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        value={
                          paginatedData[modalIndex]?.[`email${percent}`] || ""
                        }
                        onChange={(e) =>
                          handleModalChange(`email${percent}`, e.target.value)
                        }
                        disabled={salvandoAlerta}
                        placeholder={`Digite o email para alerta em ${percent}%`}
                      />
                    </div>
                  ))}
                  {salvandoAlerta && (
                    <div className="text-center mt-2">
                      <div
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Salvando...</span>
                      </div>
                      <small className="text-muted ms-2">
                        Salvando alertas...
                      </small>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={salvandoAlerta}
                  >
                    Fechar
                  </button>
                  <button
                    type="button"
                    className={`btn btn-primary ${
                      salvandoAlerta ? "disabled" : ""
                    }`}
                    onClick={salvarAlerta}
                    disabled={salvandoAlerta}
                  >
                    {salvandoAlerta ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Salvando...
                      </>
                    ) : (
                      <>🔔 Salvar Alertas</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ MODAL DE JUSTIFICATIVA ATUALIZADO COM BOTÃO SALVAR */}
        {modalJustIndex !== null && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">📝 Editar Justificativa</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeJustModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <textarea
                    className="form-control"
                    rows="10"
                    value={paginatedData[modalJustIndex]?.justificativa || ""}
                    onChange={(e) => handleJustChange(e.target.value)}
                    placeholder="Digite sua observação aqui..."
                    disabled={salvandoObservacao}
                  />
                  {salvandoObservacao && (
                    <div className="text-center mt-2">
                      <div
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Salvando...</span>
                      </div>
                      <small className="text-muted ms-2">
                        Salvando observação...
                      </small>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeJustModal}
                    disabled={salvandoObservacao}
                  >
                    Fechar
                  </button>
                  <button
                    type="button"
                    className={`btn btn-primary ${
                      salvandoObservacao ? "disabled" : ""
                    }`}
                    onClick={salvarObservacao}
                    disabled={salvandoObservacao}
                  >
                    {salvandoObservacao ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Salvando...
                      </>
                    ) : (
                      <>💾 Salvar</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ MODAL DE LISTAGEM DE MOVIMENTOS */}
        {modalListVisible !== null && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div
              className="modal-dialog modal-xl"
              style={{
                maxWidth: "min(1400px, 95vw)",
                // Teto de altura = viewport menos a margem própria do
                // .modal-dialog (1.75rem em cima e embaixo). É `maxHeight`, e
                // não `height`, para o modal continuar encolhendo quando a
                // natureza tem poucos movimentos.
                maxHeight: "calc(100% - 3.5rem)",
                // Sem isto o teto acima não vale nada: o .modal-dialog é
                // `display: block` no Bootstrap, então o .modal-content não era
                // item flex, não encolhia, e transbordava — quem passava a rolar
                // era o .modal, que cobre a viewport inteira e por isso parecia
                // a barra "da tela".
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="modal-content"
                // minHeight: 0 porque item flex tem `min-height: auto`, que o
                // impede de ficar menor que o conteúdo.
                style={{ minHeight: 0, overflow: "hidden" }}
              >
                <div className="modal-header">
                  <h5 className="modal-title">
                    📋 Listagem de Movimentos -{" "}
                    {paginatedData[modalListVisible]?.natureza}
                    <small className="text-muted ms-2">
                      ({paginatedData[modalListVisible]?.codigo})
                    </small>
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeListModal}
                  ></button>
                </div>
                {/* Coluna flex: a busca fica fixa e só a tabela rola. O
                    minHeight: 0 é o que permite este bloco encolher abaixo da
                    altura do conteúdo — sem ele o flex não cede e a tabela
                    volta a empurrar o modal para fora da tela. */}
                <div
                  className="modal-body d-flex flex-column"
                  style={{ minHeight: 0 }}
                >
                  {loadingMovimentos ? (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Carregando movimentos...
                        </span>
                      </div>
                      <p className="mt-2">Carregando movimentos...</p>
                    </div>
                  ) : movimentosData.length > 0 ? (
                    <>
                      {/* ✅ BUSCA E FILTROS (estilo em src/index.css) */}
                      {/* flex-shrink-0: quem cede altura é a tabela, não a busca */}
                      <div className="mb-3 flex-shrink-0">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <div className="busca-movimentos">
                            <span className="busca-movimentos__icone">🔍</span>
                            <input
                              type="text"
                              className="busca-movimentos__campo"
                              placeholder="Buscar em qualquer coluna — fornecedor, valor, data, tipo..."
                              value={buscaMovimentos}
                              onChange={(e) =>
                                setBuscaMovimentos(e.target.value)
                              }
                            />
                            {buscaMovimentos && (
                              <button
                                type="button"
                                className="busca-movimentos__limpar"
                                onClick={() => setBuscaMovimentos("")}
                                title="Limpar busca"
                              >
                                ✕
                              </button>
                            )}
                          </div>

                          {movimentosEstaoFiltrados && (
                            <span className="busca-movimentos-resultado">
                              <strong>{movimentosFiltrados.length}</strong> de{" "}
                              {movimentosData.length} registro(s)
                            </span>
                          )}

                          {/* âncora do menu, encostada na direita: o painel é
                              absolute em relação a ela e abre alinhado por ela */}
                          {FILTROS_HABILITADOS && (
                          <div className="filtros-movimentos-ancora">
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                filtrosAbertos || temFiltroAvancado
                                  ? "btn-primary"
                                  : "btn-outline-secondary"
                              }`}
                              onClick={() =>
                                setFiltrosAbertos((aberto) => !aberto)
                              }
                              title="Fornecedor, período, faixa de valor e mais"
                            >
                              ⚙️ Filtros
                              {temFiltroAvancado && (
                                <span className="badge bg-light text-dark ms-2">
                                  {filtrosAtivos.length}
                                </span>
                              )}
                              <span className="ms-2">
                                {filtrosAbertos ? "▲" : "▼"}
                              </span>
                            </button>

                            {filtrosAbertos && (
                              <>
                                {/* fundo invisível: clicar fora fecha o menu,
                                    sem precisar de listener no document */}
                                <div
                                  className="filtros-movimentos__fundo"
                                  onClick={() => setFiltrosAbertos(false)}
                                />
                                <div className="filtros-movimentos">
                                  <div className="filtros-movimentos__campo">
                                    <span className="filtros-movimentos__rotulo">
                                      Fornecedor
                                    </span>
                                    <select
                                      className="form-select form-select-sm"
                                      value={filtrosMovimentos.fornecedor}
                                      onChange={(e) =>
                                        alterarFiltroMovimentos(
                                          "fornecedor",
                                          e.target.value
                                        )
                                      }
                                    >
                                      <option value="">
                                        Todos ({movimentosData.length})
                                      </option>
                                      {fornecedoresMovimentos.map(
                                        (fornecedor) => (
                                          <option
                                            key={fornecedor.nome}
                                            value={fornecedor.nome}
                                          >
                                            {fornecedor.nome} (
                                            {fornecedor.quantidade})
                                          </option>
                                        )
                                      )}
                                    </select>
                                  </div>

                                  <div className="filtros-movimentos__campo">
                                    <span className="filtros-movimentos__rotulo">
                                      Período
                                    </span>
                                    <div className="filtros-movimentos__intervalo">
                                      <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={filtrosMovimentos.dataDe}
                                        onChange={(e) =>
                                          alterarFiltroMovimentos(
                                            "dataDe",
                                            e.target.value
                                          )
                                        }
                                        title="Data inicial"
                                      />
                                      <span className="filtros-movimentos__ate">
                                        →
                                      </span>
                                      <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={filtrosMovimentos.dataAte}
                                        onChange={(e) =>
                                          alterarFiltroMovimentos(
                                            "dataAte",
                                            e.target.value
                                          )
                                        }
                                        title="Data final"
                                      />
                                    </div>
                                  </div>

                                  <div className="filtros-movimentos__campo">
                                    <span className="filtros-movimentos__rotulo">
                                      Valor líquido
                                    </span>
                                    <div className="filtros-movimentos__intervalo">
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        className="form-control form-control-sm"
                                        placeholder="Mín."
                                        value={filtrosMovimentos.valorMin}
                                        onChange={(e) =>
                                          alterarFiltroMovimentos(
                                            "valorMin",
                                            e.target.value
                                          )
                                        }
                                      />
                                      <span className="filtros-movimentos__ate">
                                        →
                                      </span>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        className="form-control form-control-sm"
                                        placeholder="Máx."
                                        value={filtrosMovimentos.valorMax}
                                        onChange={(e) =>
                                          alterarFiltroMovimentos(
                                            "valorMax",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                  </div>

                                  <div className="filtros-movimentos__divisoria" />

                                  <div className="form-check form-switch filtros-movimentos__switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id="filtro-mov-com-valor"
                                      checked={
                                        filtrosMovimentos.somenteComValor
                                      }
                                      onChange={(e) =>
                                        alterarFiltroMovimentos(
                                          "somenteComValor",
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor="filtro-mov-com-valor"
                                    >
                                      Com valor líquido
                                    </label>
                                  </div>
                                  <div className="form-check form-switch filtros-movimentos__switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id="filtro-mov-com-empenhado"
                                      checked={
                                        filtrosMovimentos.somenteComEmpenhado
                                      }
                                      onChange={(e) =>
                                        alterarFiltroMovimentos(
                                          "somenteComEmpenhado",
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor="filtro-mov-com-empenhado"
                                    >
                                      Com empenhado
                                    </label>
                                  </div>

                                  <div className="filtros-movimentos__divisoria" />

                                  <div className="filtros-movimentos__acoes">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-link p-0 text-decoration-none"
                                      onClick={limparFiltrosMovimentos}
                                      disabled={!temFiltroAvancado}
                                    >
                                      🧹 Limpar
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary"
                                      onClick={() => setFiltrosAbertos(false)}
                                    >
                                      Fechar
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                          )}
                        </div>

                        {FILTROS_HABILITADOS && temFiltroAvancado && (
                          <div className="filtros-movimentos-chips mt-2">
                            {filtrosAtivos.map(({ chave, rotulo }) => (
                              <button
                                key={chave}
                                type="button"
                                className="filtro-chip"
                                onClick={() => removerFiltroMovimentos(chave)}
                                title="Remover este filtro"
                              >
                                {rotulo}
                                <span className="filtro-chip__x">✕</span>
                              </button>
                            ))}
                            <button
                              type="button"
                              className="filtro-chip filtro-chip--limpar"
                              onClick={limparFiltrosMovimentos}
                              title="Remover todos os filtros"
                            >
                              🧹 Limpar filtros
                            </button>
                          </div>
                        )}
                      </div>
                      {/* Antes: maxHeight 70vh. Como o resto do modal
                          (cabeçalho, busca, rodapé e margens) soma ~300px
                          fixos, 70vh + 300px só cabia em tela de ~1000px ou
                          mais — abaixo disso o modal estourava a janela e
                          apareciam duas barras de rolagem, uma da tabela e
                          outra do modal. Agora a tabela fica com exatamente o
                          que sobra, em qualquer altura de tela ou zoom. */}
                      <div
                        className="table-responsive"
                        style={{ minHeight: 0, overflowY: "auto" }}
                      >
                      <table className="table table-bordered table-striped table-hover align-middle">
                        <thead className="table-dark sticky-top">
                          <tr>
                            {renderThMovimento("idMov", "ID Mov", "80px")}
                            {renderThMovimento("data", "Data", "100px")}
                            {renderThMovimento("fornecedor", "Fornecedor", "200px")}
                            {renderThMovimento("valor", "Valor Líquido", "120px")}
                            {renderThMovimento("empenhado", "Empenhado", "120px")}
                            {renderThMovimento("status", "Financeiro", "80px")}
                            {renderThMovimento("tipo", "Tipo", "80px")}
                            {DOCUMENTOS_HABILITADOS && (
                              <th style={{ minWidth: "160px" }}>Documentos</th>
                            )}
                            <th style={{ minWidth: "120px" }}>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {movimentosFiltrados.length === 0 && (
                            <tr>
                              <td
                                colSpan={DOCUMENTOS_HABILITADOS ? 9 : 8}
                                className="text-center text-muted py-3"
                              >
                                Nenhum movimento corresponde aos filtros
                                aplicados.
                              </td>
                            </tr>
                          )}
                          {movimentosFiltrados.map((movimento, index) => (
                            <tr key={`${movimento.idMov}-${index}`}>
                              <td>
                                <span className="badge bg-secondary">
                                  {movimento.idMov}
                                </span>
                              </td>
                              <td>{movimento.data}</td>
                              <td>
                                <div>
                                  <strong>{movimento.fornecedor}</strong>
                                  {movimento.origem !== "N/A" && (
                                    <>
                                      <br />
                                      <small className="text-muted">
                                        Origem: {movimento.origem}
                                      </small>
                                    </>
                                  )}
                                  {movimento.destino !== "N/A" && (
                                    <>
                                      <br />
                                      <small className="text-muted">
                                        Destino: {movimento.destino}
                                      </small>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    movimento.valor >= 0
                                      ? "bg-success"
                                      : "bg-danger"
                                  }`}
                                >
                                  {movimento.valor.toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-warning text-dark">
                                  {movimento.empenhado.toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    movimento.status === "Baixado"
                                      ? "bg-success"
                                      : movimento.status === "Em Aberto"
                                      ? "bg-warning text-dark"
                                      : movimento.status === "Cancelado"
                                      ? "bg-danger"
                                      : "bg-secondary"
                                  }`}
                                >
                                  {movimento.status}
                                </span>
                              </td>
                              <td>
                                {DOCUMENTOS_HABILITADOS &&
                                movimento.tipo === "Contrato" ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary w-100"
                                    style={{ whiteSpace: "nowrap" }}
                                    title="Abrir documento do contrato"
                                    onClick={() => abrirContratoModal(movimento)}
                                  >
                                    📄 Contrato
                                  </button>
                                ) : (
                                  <small>{movimento.tipo}</small>
                                )}
                              </td>
                              {DOCUMENTOS_HABILITADOS && (
                                <td>
                                  <div className="d-grid gap-1">
                                    {renderBotaoDocumento(
                                      movimento,
                                      "nf",
                                      "🧾",
                                      "Nota Fiscal",
                                      abrirNfModal
                                    )}
                                    {renderBotaoDocumento(
                                      movimento,
                                      "comprovante",
                                      "📑",
                                      "Comprovante",
                                      abrirComprovanteModal
                                    )}
                                    {renderBotaoDocumento(
                                      movimento,
                                      "boleto",
                                      "💳",
                                      "Boleto",
                                      abrirBoletoModal
                                    )}
                                  </div>
                                </td>
                              )}
                              <td>
                                <div className="d-grid gap-1">
                                  <button
                                    style={{ whiteSpace: "nowrap" }}
                                    className={`btn btn-sm ${
                                      movimento.valor > 0
                                        ? "btn-outline-primary"
                                        : "btn-outline-secondary disabled"
                                    }`}
                                    onClick={() =>
                                      movimento.valor > 0 &&
                                      openItensModal(movimento)
                                    }
                                    disabled={movimento.valor <= 0}
                                    title={
                                      movimento.valor > 0
                                        ? "Ver detalhes dos itens"
                                        : "Itens indisponíveis para movimentos sem valor"
                                    }
                                  >
                                    📦 Itens
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="alert alert-info">
                        <i className="bi bi-info-circle"></i>
                        <strong>Nenhum movimento encontrado</strong>
                        <p className="mb-0">
                          Não foram encontrados movimentos para esta combinação
                          de centro de custo e natureza orçamentária.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <div className="me-auto">
                    <small className="text-muted d-block mb-2">
                      Total de registros:{" "}
                      <strong>{movimentosFiltrados.length}</strong>
                      {movimentosEstaoFiltrados && (
                        <span className="text-warning fw-bold ms-1">
                          (filtrado de {movimentosData.length})
                        </span>
                      )}
                    </small>
                    {movimentosFiltrados.length > 0 && (
                      <div className="totais-movimentos">
                        <span
                          className={classeTotalMovimento(
                            "realizado",
                            totaisMovimentos.realizado
                          )}
                        >
                          Total Realizado
                          <strong>
                            {moedaParaTexto(totaisMovimentos.realizado)}
                          </strong>
                        </span>
                        <span
                          className={classeTotalMovimento(
                            "empenhado",
                            totaisMovimentos.empenhado
                          )}
                        >
                          Total Empenhado
                          <strong>
                            {moedaParaTexto(totaisMovimentos.empenhado)}
                          </strong>
                        </span>
                        <span
                          className={classeTotalMovimento(
                            "total",
                            totaisMovimentos.total
                          )}
                        >
                          Total Realizado + Total Empenhado
                          <strong>
                            {moedaParaTexto(totaisMovimentos.total)}
                          </strong>
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeListModal}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Modais de documento — só existem com DOCUMENTOS_HABILITADOS */}
        {DOCUMENTOS_HABILITADOS && (
          <>
        {/* ✅ MODAL DE COMPROVANTE (GED - Itaú) */}
        {modalComprovanteVisible && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
          >
            <div
              className="modal-dialog modal-xl"
              style={{ maxWidth: "min(1000px, 95vw)" }}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">📑 Comprovante de Pagamento</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeComprovanteModal}
                  ></button>
                </div>
                <div className="modal-body p-0">
                  {loadingComprovante ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Carregando comprovante...
                        </span>
                      </div>
                      <p className="mt-2">Carregando comprovante...</p>
                    </div>
                  ) : comprovantesData.length > 0 ? (
                    <>
                      {comprovantesData.length > 1 && (
                        <div className="p-2 border-bottom">
                          <select
                            className="form-select form-select-sm"
                            value={comprovanteSelecionado}
                            onChange={(e) =>
                              setComprovanteSelecionado(Number(e.target.value))
                            }
                          >
                            {comprovantesData.map((comp, i) => (
                              <option key={comp.Id} value={i}>
                                {`#${comp.Sequencial ?? i + 1} - ${
                                  comp.TipoComprovante || "Comprovante"
                                } - R$ ${formatMoneyComprovante(
                                  comp.Valor
                                )} - ${formatDateOnly(comp.DataPagamento)}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <iframe
                        title="Comprovante Itaú"
                        srcDoc={montarHtmlComprovanteItau(
                          comprovantesData[comprovanteSelecionado]
                        )}
                        style={{ width: "100%", height: "72vh", border: 0 }}
                      />
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="alert alert-info m-3">
                        <i className="bi bi-info-circle"></i>{" "}
                        <strong>Nenhum comprovante encontrado</strong>
                        <p className="mb-0">
                          Não foi localizado comprovante vinculado a este
                          movimento no GED.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeComprovanteModal}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ MODAL DE NOTA FISCAL (Qive) */}
        {modalNfVisible && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
          >
            <div
              className="modal-dialog modal-xl"
              style={{ maxWidth: "min(1000px, 95vw)" }}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">🧾 Nota Fiscal</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeNfModal}
                  ></button>
                </div>
                <div className="modal-body p-0">
                  {loadingNf ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Carregando nota fiscal...
                        </span>
                      </div>
                      <p className="mt-2">Carregando nota fiscal...</p>
                    </div>
                  ) : notasFiscaisData.length > 0 ? (
                    <>
                      <div className="p-2 border-bottom d-flex flex-wrap gap-3 align-items-center">
                        {notasFiscaisData.length > 1 && (
                          <select
                            className="form-select form-select-sm w-auto"
                            value={nfSelecionada}
                            onChange={(e) =>
                              selecionarNf(Number(e.target.value))
                            }
                          >
                            {notasFiscaisData.map((nf, i) => (
                              <option key={nf.Id} value={i}>
                                {`NF ${nf.NumeroNota || ""} - ${
                                  nf.NomeEmitente || ""
                                } - R$ ${formatMoneyComprovante(nf.ValorTotal)}`}
                              </option>
                            ))}
                          </select>
                        )}
                        <small>
                          <strong>Nº:</strong>{" "}
                          {notasFiscaisData[nfSelecionada]?.NumeroNota || "N/A"}
                          {notasFiscaisData[nfSelecionada]?.SerieNota
                            ? ` / Série ${notasFiscaisData[nfSelecionada].SerieNota}`
                            : ""}
                        </small>
                        <small>
                          <strong>Emitente:</strong>{" "}
                          {notasFiscaisData[nfSelecionada]?.NomeEmitente ||
                            "N/A"}
                        </small>
                        <small>
                          <strong>Emissão:</strong>{" "}
                          {formatDateOnly(
                            notasFiscaisData[nfSelecionada]?.DataEmissao
                          )}
                        </small>
                        <small>
                          <strong>Valor:</strong> R${" "}
                          {formatMoneyComprovante(
                            notasFiscaisData[nfSelecionada]?.ValorTotal
                          )}
                        </small>
                        {notasFiscaisData[nfSelecionada]?.Cancelada && (
                          <span className="badge bg-danger">Cancelada</span>
                        )}
                      </div>
                      {nfPdfUrl ? (
                        <iframe
                          title="Nota Fiscal PDF"
                          src={nfPdfUrl}
                          style={{ width: "100%", height: "70vh", border: 0 }}
                        />
                      ) : (
                        <div className="text-center py-4">
                          <div className="alert alert-warning m-3">
                            <strong>PDF (DANFE) indisponível</strong>
                            <p className="mb-0">
                              Esta nota não possui PDF armazenado. Use o botão
                              abaixo para baixar o XML.
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="alert alert-info m-3">
                        <i className="bi bi-info-circle"></i>{" "}
                        <strong>Nenhuma nota fiscal encontrada</strong>
                        <p className="mb-0">
                          Não foi localizada nota fiscal vinculada a este
                          movimento.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  {notasFiscaisData.length > 0 && (
                    <div className="me-auto d-flex gap-2">
                      {nfPdfUrl && (
                        <a
                          href={nfPdfUrl}
                          download={`nf_${
                            notasFiscaisData[nfSelecionada]?.NumeroNota ||
                            notasFiscaisData[nfSelecionada]?.Id
                          }.pdf`}
                          className="btn btn-outline-primary"
                        >
                          ⬇️ Baixar DANFE (PDF)
                        </a>
                      )}
                      {notasFiscaisData[nfSelecionada]?.TemXml && (
                        <button
                          type="button"
                          onClick={() =>
                            baixarNfXml(notasFiscaisData[nfSelecionada])
                          }
                          className="btn btn-outline-secondary"
                        >
                          ⬇️ Baixar XML
                        </button>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeNfModal}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ MODAL DE BOLETO (Qive) */}
        {modalBoletoVisible && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
          >
            <div
              className="modal-dialog modal-xl"
              style={{ maxWidth: "min(1000px, 95vw)" }}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">💳 Boleto</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeBoletoModal}
                  ></button>
                </div>
                <div className="modal-body p-0">
                  {loadingBoleto ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Carregando boleto...
                        </span>
                      </div>
                      <p className="mt-2">Carregando boleto...</p>
                    </div>
                  ) : boletosData.length > 0 ? (
                    <>
                      <div className="p-2 border-bottom d-flex flex-wrap gap-3 align-items-center">
                        {boletosData.length > 1 && (
                          <select
                            className="form-select form-select-sm w-auto"
                            value={boletoSelecionado}
                            onChange={(e) =>
                              selecionarBoleto(Number(e.target.value))
                            }
                          >
                            {boletosData.map((boleto, i) => (
                              <option key={boleto.Id} value={i}>
                                {`Venc. ${formatDateOnly(
                                  boleto.DataVencimento
                                )} - R$ ${formatMoneyComprovante(
                                  boleto.Valor
                                )} - ${boleto.Status || ""}`}
                              </option>
                            ))}
                          </select>
                        )}
                        <small>
                          <strong>Beneficiário:</strong>{" "}
                          {boletosData[boletoSelecionado]?.BeneficiarioNome ||
                            "N/A"}
                        </small>
                        <small>
                          <strong>Vencimento:</strong>{" "}
                          {formatDateOnly(
                            boletosData[boletoSelecionado]?.DataVencimento
                          )}
                        </small>
                        <small>
                          <strong>Valor:</strong> R${" "}
                          {formatMoneyComprovante(
                            boletosData[boletoSelecionado]?.Valor
                          )}
                        </small>
                        <span
                          className={`badge ${
                            boletosData[boletoSelecionado]?.Status === "paid"
                              ? "bg-success"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {boletosData[boletoSelecionado]?.Status === "paid"
                            ? "Pago"
                            : "Em aberto"}
                        </span>
                      </div>
                      {boletoPdfUrl ? (
                        <iframe
                          title="Boleto PDF"
                          src={boletoPdfUrl}
                          style={{ width: "100%", height: "70vh", border: 0 }}
                        />
                      ) : (
                        <div className="text-center py-4">
                          <div className="alert alert-warning m-3">
                            <strong>PDF indisponível</strong>
                            <p className="mb-0">
                              Este boleto não possui PDF armazenado.
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <div className="alert alert-info m-3">
                        <i className="bi bi-info-circle"></i>{" "}
                        <strong>Nenhum boleto encontrado</strong>
                        <p className="mb-0">
                          Não foi localizado boleto vinculado a este movimento.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  {boletoPdfUrl && (
                    <a
                      href={boletoPdfUrl}
                      download={`boleto_${
                        boletosData[boletoSelecionado]?.Id || "download"
                      }.pdf`}
                      className="btn btn-outline-primary me-auto"
                    >
                      ⬇️ Baixar PDF
                    </a>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeBoletoModal}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ✅ MODAL DE CONTRATO (documento assinado) */}
        {modalContratoVisible && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
          >
            <div
              className="modal-dialog modal-xl"
              style={{ maxWidth: "min(1000px, 95vw)" }}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    📄 Contrato
                    {contratoMovAtual?.fornecedor
                      ? ` - ${contratoMovAtual.fornecedor}`
                      : ""}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeContratoModal}
                  ></button>
                </div>
                <div className="modal-body p-0">
                  {loadingContrato ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Carregando contrato...
                        </span>
                      </div>
                      <p className="mt-2">Carregando contrato...</p>
                    </div>
                  ) : contratoErro ? (
                    <div className="text-center py-4">
                      <div className="alert alert-info m-3">
                        <i className="bi bi-info-circle"></i>{" "}
                        <strong>Documento indisponível</strong>
                        <p className="mb-0">{contratoErro}</p>
                      </div>
                    </div>
                  ) : contratoPdfUrl ? (
                    <iframe
                      title="Contrato PDF"
                      src={contratoPdfUrl}
                      style={{ width: "100%", height: "70vh", border: 0 }}
                    />
                  ) : null}
                </div>
                <div className="modal-footer">
                  {contratoPdfUrl && (
                    <a
                      href={contratoPdfUrl}
                      download={`contrato_${
                        contratoMovAtual?.idMov || "download"
                      }.pdf`}
                      className="btn btn-outline-primary me-auto"
                    >
                      ⬇️ Baixar PDF
                    </a>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeContratoModal}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
          </>
        )}
        {/* ✅ MODAL DE ITENS DO MOVIMENTO */}
        {modalItensVisible !== null && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    📦 Itens do Movimento - {modalItensVisible.destino} -{" "}
                    {modalItensVisible.fornecedor}
                    <small className="text-muted ms-2">
                      (ID: {modalItensVisible.idMov} | Data:{" "}
                      {modalItensVisible.data})
                    </small>
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeItensModal}
                  ></button>
                </div>
                <div className="modal-body">
                  {loadingItens ? (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Carregando itens...
                        </span>
                      </div>
                      <p className="mt-2">Carregando itens do movimento...</p>
                    </div>
                  ) : itensMovimento.length > 0 ? (
                    <div
                      className="table-responsive"
                      style={{ maxHeight: "60vh", overflowY: "auto" }}
                    >
                      <table className="table table-bordered table-striped table-hover">
                        <thead className="table-dark sticky-top">
                          <tr>
                            <th style={{ minWidth: "100px" }}>Código</th>
                            <th style={{ minWidth: "250px" }}>Descrição</th>
                            <th style={{ minWidth: "80px" }}>Qtd</th>
                            <th style={{ minWidth: "60px" }}>Unidade</th>
                            <th style={{ minWidth: "120px" }}>Valor Unit.</th>
                            <th style={{ minWidth: "120px" }}>Valor Total</th>
                            <th style={{ minWidth: "100px" }}>NF</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itensMovimento.map((item, index) => (
                            <tr key={`${item.idItem}-${index}`}>
                              <td>
                                <span className="badge bg-secondary">
                                  {item.codProduto}
                                </span>
                                <br />
                                <small className="text-muted">
                                  {item.idItem}
                                </small>
                              </td>
                              <td>
                                <div>
                                  <strong>{item.descricao}</strong>
                                  {item.observacao && (
                                    <>
                                      <br />
                                      <small className="text-muted">
                                        📝 {item.observacao}
                                      </small>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-info text-dark">
                                  {item.quantidade.toLocaleString("pt-BR", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 3,
                                  })}
                                </span>
                              </td>
                              <td className="text-center">
                                <small>{item.unidade}</small>
                              </td>
                              <td className="text-end">
                                <span className="badge bg-warning text-dark">
                                  {item.valorUnitario.toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })}
                                </span>
                              </td>
                              <td className="text-end">
                                <span
                                  className={`badge ${
                                    item.valorTotal >= 0
                                      ? "bg-success"
                                      : "bg-danger"
                                  }`}
                                >
                                  {item.valorTotal.toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })}
                                </span>
                              </td>
                              <td>
                                <small>
                                  {item.numeroNF !== "N/A" && (
                                    <>
                                      NF: {item.numeroNF}
                                      {item.serieNF !== "N/A" && <br />}
                                      {item.serieNF !== "N/A" && (
                                        <>Série: {item.serieNF}</>
                                      )}
                                    </>
                                  )}
                                </small>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="alert alert-warning">
                        <i className="bi bi-exclamation-triangle"></i>
                        <strong>Nenhum item encontrado</strong>
                        <p className="mb-0">
                          Não foram encontrados itens para este movimento.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <div className="me-auto">
                    <small className="text-muted">
                      Total de itens: <strong>{itensMovimento.length}</strong>
                      {itensMovimento.length > 0 && (
                        <>
                          <span className="ms-3">
                            Qtd total:{" "}
                            <strong>
                              {itensMovimento
                                .reduce((acc, item) => acc + item.quantidade, 0)
                                .toLocaleString("pt-BR", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 3,
                                })}
                            </strong>
                          </span>
                          <span className="ms-3">
                            Valor total:{" "}
                            <strong>
                              {itensMovimento
                                .reduce((acc, item) => acc + item.valorTotal, 0)
                                .toLocaleString("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                })}
                            </strong>
                          </span>
                        </>
                      )}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeItensModal}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ MODAL DE HISTÓRICO DE VERSÕES */}
        {modalVersaoVisible !== null && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    🕓 Histórico de Versões - {modalVersaoVisible.natureza}
                    <small className="text-muted ms-2">
                      (ID Orçamento: {modalVersaoVisible.idOrcto})
                    </small>
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeVersaoModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    {/* Lista de Versões */}
                    <div className={versaoDetalhada ? "col-md-6" : "col-12"}>
                      {loadingVersoes ? (
                        <div className="text-center py-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">
                              Carregando versões...
                            </span>
                          </div>
                          <p className="mt-2">
                            Carregando histórico de versões...
                          </p>
                        </div>
                      ) : versoesData.length > 0 ? (
                        <div>
                          <h6 className="mb-3">📋 Versões Disponíveis</h6>
                          <div
                            className="list-group"
                            style={{ maxHeight: "60vh", overflowY: "auto" }}
                          >
                            {versoesData.map((versao, index) => (
                              <div
                                key={`${versao.id}-${index}`}
                                className={`list-group-item list-group-item-action ${
                                  versaoDetalhada &&
                                  versaoDetalhada.id === versao.id
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() => verDetalhesVersao(versao)}
                                style={{ cursor: "pointer" }}
                              >
                                <div className="d-flex w-100 justify-content-between">
                                  <h6 className="mb-1">
                                    Versão {versao.versao}
                                    {index === 0 && (
                                      <span className="badge bg-success ms-2">
                                        Atual
                                      </span>
                                    )}
                                  </h6>
                                  <small>{versao.data}</small>
                                </div>
                                <p className="mb-1">
                                  <strong>Usuário:</strong> {versao.nome}
                                </p>
                                <small className="text-muted">
                                  Clique para ver detalhes
                                </small>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <div className="alert alert-info">
                            <i className="bi bi-info-circle"></i>
                            <strong>Nenhuma versão encontrada</strong>
                            <p className="mb-0">
                              Não há histórico de versões para este orçamento.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Detalhes da Versão */}
                    {versaoDetalhada && (
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-header bg-primary text-white">
                            <h6 className="mb-0">
                              {versaoDetalhada.versao1
                                ? "📊 Comparação de Versões"
                                : "📄 Detalhes da Versão"}
                            </h6>
                          </div>
                          <div className="card-body">
                            {versaoDetalhada.versao1 ? (
                              // Modo comparação
                              <div>
                                <div className="row">
                                  <div className="col-6">
                                    <h6 className="text-center text-primary">
                                      Versão {versaoDetalhada.versao1.versao}
                                    </h6>
                                  </div>
                                  <div className="col-6">
                                    <h6 className="text-center text-success">
                                      Versão {versaoDetalhada.versao2.versao}
                                    </h6>
                                  </div>
                                </div>

                                <table className="table table-sm table-bordered mt-3">
                                  <tbody>
                                    <tr
                                      className={
                                        versaoDetalhada.diferencas.usuario
                                          ? "table-warning"
                                          : ""
                                      }
                                    >
                                      <td>
                                        <strong>Usuário</strong>
                                      </td>
                                      <td>{versaoDetalhada.versao1.nome}</td>
                                      <td>{versaoDetalhada.versao2.nome}</td>
                                    </tr>
                                    <tr
                                      className={
                                        versaoDetalhada.diferencas.data
                                          ? "table-warning"
                                          : ""
                                      }
                                    >
                                      <td>
                                        <strong>Data</strong>
                                      </td>
                                      <td>{versaoDetalhada.versao1.data}</td>
                                      <td>{versaoDetalhada.versao2.data}</td>
                                    </tr>
                                  </tbody>
                                </table>

                                {versaoDetalhada.diferencas.valor && (
                                  <div className="alert alert-info mt-3">
                                    <strong>
                                      ⚠️ Alterações detectadas no conteúdo SQL
                                    </strong>
                                  </div>
                                )}
                              </div>
                            ) : (
                              // Modo detalhes
                              <div>
                                <dl className="row">
                                  <dt className="col-sm-4">Versão:</dt>
                                  <dd className="col-sm-8">
                                    <span className="badge bg-primary">
                                      {versaoDetalhada.versao}
                                    </span>
                                  </dd>

                                  <dt className="col-sm-4">Usuário:</dt>
                                  <dd className="col-sm-8">
                                    {versaoDetalhada.nome}
                                  </dd>

                                  <dt className="col-sm-4">Data/Hora:</dt>
                                  <dd className="col-sm-8">
                                    {versaoDetalhada.data}
                                  </dd>

                                  <dt className="col-sm-4">ID Orçamento:</dt>
                                  <dd className="col-sm-8">
                                    <code>{versaoDetalhada.idOrcto}</code>
                                  </dd>
                                </dl>

                                {versaoDetalhada.valor && (
                                  <div className="mt-3">
                                    <h6>📝Valor:</h6>
                                    <div
                                      className="bg-light p-3 rounded"
                                      style={{
                                        maxHeight: "300px",
                                        overflowY: "auto",
                                      }}
                                    >
                                      <pre className="mb-0">
                                        <code>{versaoDetalhada.valor}</code>
                                      </pre>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="me-auto">
                    <small className="text-muted">
                      Total de versões: <strong>{versoesData.length}</strong>
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeVersaoModal}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ MODAL DE DADOS DO ANO ANTERIOR */}
        {modalAnoAnteriorVisible !== null && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header bg-info text-white">
                  <h5 className="modal-title">
                    📅 Dados do Ano Anterior - {dadosAnoAnterior?.natureza}
                    <small className="ms-2">
                      ({dadosAnoAnterior?.codigo} - Ano {dadosAnoAnterior?.ano})
                    </small>
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={fecharModalAnoAnterior}
                  ></button>
                </div>
                <div className="modal-body">
                  {loadingAnoAnterior ? (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Carregando dados do ano anterior...
                        </span>
                      </div>
                      <p className="mt-2">
                        Carregando dados do ano anterior...
                      </p>
                    </div>
                  ) : dadosAnoAnterior ? (
                    <div>
                      {/* Informações básicas */}
                      <div className="card mb-3">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">ℹ️ Informações Básicas</h6>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <strong>Natureza:</strong>{" "}
                              {dadosAnoAnterior.natureza}
                            </div>
                            <div className="col-md-6">
                              <strong>Código:</strong> {dadosAnoAnterior.codigo}
                            </div>
                            <div className="col-md-12 mt-2">
                              <strong>Centro de Custo:</strong>{" "}
                              {dadosAnoAnterior.centroCusto}
                            </div>
                          </div>
                        </div>
                      </div>

                      {dadosAnoAnterior.semDados ? (
                        <div className="alert alert-warning text-center">
                          <i className="bi bi-exclamation-triangle"></i>
                          <h6>
                            Nenhum dado encontrado para o ano{" "}
                            {dadosAnoAnterior.ano}
                          </h6>
                          <p className="mb-0">
                            Não foram encontrados registros para esta natureza
                            orçamentária no ano anterior.
                          </p>
                        </div>
                      ) : (
                        <div>
                          {/* Cards com os valores principais */}
                          <div className="row g-3 mb-4">
                            {/* Orçado */}
                            <div className="col-md-4">
                              <div className="card border-primary">
                                <div className="card-body text-center">
                                  <h6 className="card-title text-primary">
                                    💰 Orçado
                                  </h6>
                                  <h5 className="text-primary">
                                    {dadosAnoAnterior.orcado.toLocaleString(
                                      "pt-BR",
                                      {
                                        style: "currency",
                                        currency: "BRL",
                                      }
                                    )}
                                  </h5>
                                </div>
                              </div>
                            </div>

                            {/* Realizado */}
                            <div className="col-md-4">
                              <div className="card border-danger">
                                <div className="card-body text-center">
                                  <h6 className="card-title text-danger">
                                    📊 Realizado
                                  </h6>
                                  <h5 className="text-danger">
                                    {dadosAnoAnterior.realizado.toLocaleString(
                                      "pt-BR",
                                      {
                                        style: "currency",
                                        currency: "BRL",
                                      }
                                    )}
                                  </h5>
                                </div>
                              </div>
                            </div>

                            {/* Economia 
                            <div className="col-md-3">
                              <div className="card border-success">
                                <div className="card-body text-center">
                                  <h6 className="card-title text-success">
                                    💎 Economia
                                  </h6>
                                  <h5 className="text-success">
                                    {dadosAnoAnterior.economia.toLocaleString(
                                      "pt-BR",
                                      {
                                        style: "currency",
                                        currency: "BRL",
                                      }
                                    )}
                                  </h5>
                                </div>
                              </div>
                            </div>
                            */}

                            {/* Eficiente */}
                            <div className="col-md-4">
                              <div className="card border-warning">
                                <div className="card-body text-center">
                                  <h6 className="card-title text-warning">
                                    ⚡ Executado
                                  </h6>
                                  <h5 className="text-warning">
                                    {dadosAnoAnterior.eficiente} %
                                  </h5>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Informações calculadas */}
                          <div className="row g-3">
                            {/* Saldo */}
                            <div className="col-md-4">
                              <div
                                className={`card ${
                                  dadosAnoAnterior.saldo >= 0
                                    ? "border-success"
                                    : "border-danger"
                                }`}
                              >
                                <div className="card-body text-center">
                                  <h6 className="card-title">💳 Saldo</h6>
                                  <h5
                                    className={
                                      dadosAnoAnterior.saldo >= 0
                                        ? "text-success"
                                        : "text-danger"
                                    }
                                  >
                                    {dadosAnoAnterior.saldo.toLocaleString(
                                      "pt-BR",
                                      {
                                        style: "currency",
                                        currency: "BRL",
                                      }
                                    )}
                                  </h5>
                                </div>
                              </div>
                            </div>

                            {/* Consumo */}
                            <div className="col-md-8">
                              <div className="card">
                                <div className="card-body">
                                  <h6 className="card-title">
                                    📈 Consumo do Orçamento
                                  </h6>
                                  <div
                                    className="progress"
                                    style={{ height: "2rem" }}
                                  >
                                    <div
                                      className={`progress-bar ${
                                        dadosAnoAnterior.consumoPercent > 80
                                          ? "bg-danger"
                                          : dadosAnoAnterior.consumoPercent > 50
                                          ? "bg-warning"
                                          : "bg-success"
                                      }`}
                                      role="progressbar"
                                      style={{
                                        width: `${Math.min(
                                          100,
                                          dadosAnoAnterior.consumoPercent
                                        )}%`,
                                      }}
                                      aria-valuenow={
                                        dadosAnoAnterior.consumoPercent
                                      }
                                      aria-valuemin="0"
                                      aria-valuemax="100"
                                    >
                                      <strong>
                                        {dadosAnoAnterior.consumoPercent}%
                                      </strong>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Comparação com ano atual */}
                          {paginatedData[modalAnoAnteriorVisible] && (
                            <div className="card mt-4">
                              <div className="card-header bg-secondary text-white">
                                <h6 className="mb-0">
                                  🔄 Comparação com Ano Atual
                                </h6>
                              </div>
                              <div className="card-body">
                                <div className="table-responsive">
                                  <table className="table table-bordered table-sm">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Métrica</th>
                                        <th>
                                          Ano Anterior ({dadosAnoAnterior.ano})
                                        </th>
                                        <th>
                                          Ano Atual (
                                          {localStorage.getItem("selectedYear")}
                                          )
                                        </th>
                                        <th>Variação</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td>
                                          <strong>Orçado</strong>
                                        </td>
                                        <td>
                                          {dadosAnoAnterior.orcado.toLocaleString(
                                            "pt-BR",
                                            {
                                              style: "currency",
                                              currency: "BRL",
                                            }
                                          )}
                                        </td>
                                        <td>
                                          {paginatedData[
                                            modalAnoAnteriorVisible
                                          ].orcado.toLocaleString("pt-BR", {
                                            style: "currency",
                                            currency: "BRL",
                                          })}
                                        </td>
                                        <td>
                                          <span
                                            className={`badge ${
                                              paginatedData[
                                                modalAnoAnteriorVisible
                                              ].orcado > dadosAnoAnterior.orcado
                                                ? "bg-danger"
                                                : "bg-success"
                                            }`}
                                          >
                                            {paginatedData[
                                              modalAnoAnteriorVisible
                                            ].orcado > dadosAnoAnterior.orcado
                                              ? "📈"
                                              : "📉"}
                                            {(
                                              ((paginatedData[
                                                modalAnoAnteriorVisible
                                              ].orcado -
                                                dadosAnoAnterior.orcado) /
                                                (dadosAnoAnterior.orcado ||
                                                  1)) *
                                              100
                                            ).toFixed(1)}
                                            %
                                          </span>
                                        </td>
                                      </tr>
                                      <tr>
                                        <td>
                                          <strong>Realizado</strong>
                                        </td>
                                        <td>
                                          {dadosAnoAnterior.realizado.toLocaleString(
                                            "pt-BR",
                                            {
                                              style: "currency",
                                              currency: "BRL",
                                            }
                                          )}
                                        </td>
                                        <td>
                                          {paginatedData[
                                            modalAnoAnteriorVisible
                                          ].realizado.toLocaleString("pt-BR", {
                                            style: "currency",
                                            currency: "BRL",
                                          })}
                                        </td>
                                        <td>
                                          <span
                                            className={`badge ${
                                              paginatedData[
                                                modalAnoAnteriorVisible
                                              ].realizado >
                                              dadosAnoAnterior.realizado
                                                ? "bg-danger"
                                                : "bg-success"
                                            }`}
                                          >
                                            {paginatedData[
                                              modalAnoAnteriorVisible
                                            ].realizado >
                                            dadosAnoAnterior.realizado
                                              ? "📈"
                                              : "📉"}
                                            {(
                                              ((paginatedData[
                                                modalAnoAnteriorVisible
                                              ].realizado -
                                                dadosAnoAnterior.realizado) /
                                                (dadosAnoAnterior.realizado ||
                                                  1)) *
                                              100
                                            ).toFixed(1)}
                                            %
                                          </span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="alert alert-danger">
                        <i className="bi bi-exclamation-triangle"></i>
                        <strong>Erro ao carregar dados</strong>
                        <p className="mb-0">
                          Não foi possível carregar os dados do ano anterior.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <div className="me-auto">
                    <small className="text-muted">
                      {dadosAnoAnterior && !dadosAnoAnterior.semDados && (
                        <>
                          Dados do ano <strong>{dadosAnoAnterior.ano}</strong> |
                          Consumo:{" "}
                          <strong>{dadosAnoAnterior.consumoPercent}%</strong>
                        </>
                      )}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={fecharModalAnoAnterior}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ MODAL DE LOADING PARA RELATÓRIOS */}
        {relatorioLoading && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.8)", zIndex: 9999 }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header bg-primary text-white border-0">
                  <h5 className="modal-title d-flex align-items-center">
                    <div
                      className="spinner-grow spinner-grow-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    📊 Gerando Relatório {relatorioNome}
                  </h5>
                  {relatorioResultado && !relatorioResultado.sucesso && (
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={fecharModalRelatorio}
                    ></button>
                  )}
                </div>

                <div className="modal-body text-center py-4">
                  {/* Animação de Loading */}
                  <div className="mb-4">
                    <div className="position-relative d-inline-block">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                        style={{ width: "4rem", height: "4rem" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div
                        className="position-absolute top-50 start-50 translate-middle"
                        style={{ fontSize: "1.5rem" }}
                      >
                        📊
                      </div>
                    </div>
                  </div>

                  {/* Barra de Progresso */}
                  <div className="mb-3">
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className={`progress-bar progress-bar-striped progress-bar-animated ${
                          relatorioProgresso === 100
                            ? relatorioResultado?.sucesso
                              ? "bg-success"
                              : "bg-danger"
                            : "bg-primary"
                        }`}
                        role="progressbar"
                        style={{ width: `${relatorioProgresso}%` }}
                        aria-valuenow={relatorioProgresso}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                    <small className="text-muted">
                      {relatorioProgresso.toFixed(0)}% concluído
                    </small>
                  </div>

                  {/* Mensagem Status */}
                  <div className="mb-3">
                    <p className="mb-1 fw-medium text-primary">
                      {relatorioMensagem}
                    </p>
                    {relatorioResultado && (
                      <div
                        className={`alert ${
                          relatorioResultado.sucesso
                            ? "alert-success"
                            : "alert-danger"
                        } mt-3`}
                      >
                        {relatorioResultado.mensagem}
                      </div>
                    )}
                  </div>

                  {/* Dicas Motivacionais */}
                  {!relatorioResultado && (
                    <div className="mt-4 p-3 bg-light rounded">
                      <h6 className="text-muted mb-2">
                        💡 Enquanto você aguarda...
                      </h6>
                      <small className="text-muted">
                        {relatorioTipo === "lancamentos-analitico"
                          ? "O relatório analítico fornece informações detalhadas para análise profunda dos dados orçamentários."
                          : "O relatório sintético oferece uma visão consolidada e resumida dos principais indicadores."}
                      </small>
                    </div>
                  )}

                  {/* Informações Técnicas */}
                  <div className="mt-3">
                    <small className="text-muted d-block">
                      🏢 Filial: {localStorage.getItem("NomeFilial") || "N/A"}
                    </small>
                    <small className="text-muted d-block">
                      📅 Período:{" "}
                      {localStorage.getItem("selectedYear") ||
                        new Date().getFullYear()}
                    </small>
                    <small className="text-muted d-block">
                      👤 Usuário: {localStorage.getItem("username") || "N/A"}
                    </small>
                  </div>
                </div>

                {/* Footer apenas para erros */}
                {relatorioResultado && !relatorioResultado.sucesso && (
                  <div className="modal-footer border-0">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={fecharModalRelatorio}
                    >
                      Fechar
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        fecharModalRelatorio();
                        // Tentar novamente
                        setTimeout(() => {
                          if (relatorioTipo === "lancamentos-analitico") {
                            iniciarGeracaoRelatorio(
                              "lancamentos-analitico",
                              "Analítico"
                            );
                          } else {
                            iniciarGeracaoRelatorio(
                              "lancamentos-sintetico",
                              "Sintético"
                            );
                          }
                        }, 100);
                      }}
                    >
                      🔄 Tentar Novamente
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
